import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Crop,
  Maximize2,
  Sliders,
  Type,
  Paintbrush,
  Smile,
  SunMedium,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  RotateCw,
  Download,
  Upload,
  FlipHorizontal,
  FlipVertical,
  Trash2,
  Lock,
  Unlock,
  Save,
  Loader2,
  ArrowLeft,
  Undo2,
  Redo2,
  FolderOpen,
  Square,
  Circle,
  ArrowRight,
  Minus,
  Scissors,
  Eraser,
  LayoutGrid,
  Wand2,
  Frame,
  Plus,
  ImageIcon,
  Sparkles,
  MessageSquare,
  Palette,
  Copy,
  Check,
  ArrowUpRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../store/themeStore';
import { mediaAPI, aiAPI, aiImagesAPI, getErrorMessage, formatBytes } from '../../services/api';
import MediaPickerModal from './MediaPickerModal';

/* ─── Google Fonts (lazy-injected) ─── */
const FONT_FAMILIES = [
  { label: 'Default', value: 'Arial' },
  { label: 'Inter', value: 'Inter' },
  { label: 'Roboto', value: 'Roboto' },
  { label: 'Playfair', value: 'Playfair Display' },
  { label: 'Oswald', value: 'Oswald' },
  { label: 'Raleway', value: 'Raleway' },
  { label: 'Bebas Neue', value: 'Bebas Neue' },
  { label: 'Dancing Script', value: 'Dancing Script' },
  { label: 'Pacifico', value: 'Pacifico' },
];

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=Roboto:wght@400;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Oswald:wght@400;700&family=Raleway:wght@400;700&family=Bebas+Neue&family=Dancing+Script:wght@400;700&family=Pacifico&display=swap';

function ensureGoogleFonts() {
  if (document.getElementById('editor-gfonts')) return;
  const link = document.createElement('link');
  link.id = 'editor-gfonts';
  link.rel = 'stylesheet';
  link.href = GOOGLE_FONTS_URL;
  document.head.appendChild(link);
}

/* ─── Types ─── */
interface TextElement {
  id: string;
  text: string;
  x: number;
  y: number;
  fontSize: number;
  color: string;
  fontFamily: string;
  bold: boolean;
  italic: boolean;
  // Tier-2: shadow & stroke
  shadowBlur: number;     // 0–20
  shadowColor: string;
  shadowOffsetX: number;  // -20 to 20
  shadowOffsetY: number;
  strokeColor: string;
  strokeWidth: number;    // 0–10
}

interface EditorBrandKit {
  id: string;
  name: string;
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_colors?: string[] | null;
  primary_font?: string | null;
  secondary_font?: string | null;
  is_default?: boolean;
}

type ShapeKind = 'rect' | 'circle' | 'arrow' | 'line';
interface ShapeElement {
  id: string;
  kind: ShapeKind;
  x: number;
  y: number;
  w: number;
  h: number;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  opacity: number;
}

/* Eraser path (destination-out compositing on export) */
interface EraserPath {
  points: { x: number; y: number }[];
  width: number;
}

/* Frame / border config */
type FrameStyle = 'solid' | 'gradient-lr' | 'gradient-tb' | 'double';
interface FrameConfig {
  enabled: boolean;
  style: FrameStyle;
  color1: string;
  color2: string;
  width: number; // px relative to output canvas
  opacity: number;
}

/* Collage */
type CollageLayout = '1+1' | '2+1' | 'grid-4';
interface CollageSlot {
  id: string;
  src: string | null; // dataUrl
  label: string;
}

interface EditState {
  brightness: number;
  contrast: number;
  saturation: number;
  blur: number;
  hueRotate: number;
  opacity: number;
  invert: boolean;
  rotate: number;
  flipH: boolean;
  flipV: boolean;
  zoom: number;
  width: number;
  height: number;
  // New Tier-1 adjustments
  vignetteIntensity: number; // 0–100
  sharpen: number;           // 0–100
  noise: number;             // 0–100
}

type ToolType = 'crop' | 'resize' | 'filters' | 'text' | 'draw' | 'stickers' | 'adjust' | 'shapes' | 'bg-remove' | 'eraser' | 'collage' | 'templates' | 'frame' | null;
type FilterPresetName = 'none' | 'grayscale' | 'sepia' | 'warm' | 'cool' | 'vivid' | 'dramatic' | 'vintage' | 'fade';

interface CropRegion {
  x: number; y: number; w: number; h: number;
}

interface DrawPath {
  points: { x: number; y: number }[];
  color: string;
  width: number;
}

/* ─── Defaults ─── */
const defaultEdit: EditState = {
  brightness: 100, contrast: 100, saturation: 100, blur: 0,
  hueRotate: 0, opacity: 100, invert: false,
  rotate: 0, flipH: false, flipV: false, zoom: 1,
  width: 0, height: 0,
  vignetteIntensity: 0, sharpen: 0, noise: 0,
};

/* ─── Pixel-level effects ─── */
function applyPixelEffects(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  sharpen: number,
  noise: number,
) {
  if (sharpen === 0 && noise === 0) return;
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  if (sharpen > 0) {
    const amount = sharpen / 100;
    // Simple unsharp mask kernel
    const kernel = [0, -1, 0, -1, 4 + 1 / amount, -1, 0, -1, 0];
    const src = new Uint8ClampedArray(data);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = (y * w + x) * 4;
        for (let c = 0; c < 3; c++) {
          let val = 0;
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const ni = ((y + ky) * w + (x + kx)) * 4 + c;
              val += src[ni] * kernel[(ky + 1) * 3 + (kx + 1)];
            }
          }
          data[i + c] = Math.max(0, Math.min(255, val));
        }
      }
    }
  }

  if (noise > 0) {
    const amount = (noise / 100) * 50;
    for (let i = 0; i < data.length; i += 4) {
      const rnd = (Math.random() - 0.5) * amount;
      data[i] = Math.max(0, Math.min(255, data[i] + rnd));
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + rnd));
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + rnd));
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

/* ─── Filter presets ─── */
const getFilterPresets = (t: (key: string, fallback: string) => string): { name: FilterPresetName; label: string; overrides: Partial<EditState> }[] => [
  { name: 'none', label: t('editor_filter_normal', 'Normal'), overrides: { brightness: 100, contrast: 100, saturation: 100, blur: 0, hueRotate: 0, invert: false } },
  { name: 'grayscale', label: t('editor_filter_bw', 'B&W'), overrides: { saturation: 0 } },
  { name: 'sepia', label: t('editor_filter_sepia', 'Sepia'), overrides: { saturation: 50, hueRotate: 30, brightness: 95 } },
  { name: 'warm', label: t('editor_filter_warm', 'Warm'), overrides: { hueRotate: -15, saturation: 120, brightness: 105 } },
  { name: 'cool', label: t('editor_filter_cool', 'Cool'), overrides: { hueRotate: 15, saturation: 90, brightness: 98 } },
  { name: 'vivid', label: t('editor_filter_vivid', 'Vivid'), overrides: { saturation: 160, contrast: 115 } },
  { name: 'dramatic', label: t('editor_filter_dramatic', 'Dramatic'), overrides: { contrast: 140, brightness: 90, saturation: 80 } },
  { name: 'vintage', label: t('editor_filter_vintage', 'Vintage'), overrides: { saturation: 60, contrast: 90, brightness: 95, hueRotate: 15 } },
  { name: 'fade', label: t('editor_filter_fade', 'Fade'), overrides: { contrast: 80, brightness: 110, saturation: 80 } },
];

const getCropPresets = (t: (key: string, fallback: string) => string) => [
  { label: t('editor_crop_free', 'Free'), ratio: 0 },
  { label: '1:1', ratio: 1 },
  { label: '4:5', ratio: 4 / 5 },
  { label: '9:16', ratio: 9 / 16 },
  { label: '16:9', ratio: 16 / 9 },
];

const getPlatformSizes = (t: (key: string, fallback: string) => string) => [
  { label: t('editor_platform_instagram_post', 'Instagram Post'), w: 1080, h: 1080 },
  { label: t('editor_platform_instagram_story', 'Instagram Story'), w: 1080, h: 1920 },
  { label: t('editor_platform_facebook_cover', 'Facebook Cover'), w: 820, h: 312 },
  { label: t('editor_platform_twitter_header', 'Twitter Header'), w: 1500, h: 500 },
  { label: t('editor_platform_youtube_thumb', 'YouTube Thumb'), w: 1280, h: 720 },
];

function formatPlatformName(platform: string): string {
  switch (platform) {
    case 'instagram':
      return 'Instagram';
    case 'facebook':
      return 'Facebook';
    case 'twitter':
      return 'Twitter/X';
    case 'tiktok':
      return 'TikTok';
    case 'linkedin':
      return 'LinkedIn';
    case 'youtube':
      return 'YouTube';
    case 'pinterest':
      return 'Pinterest';
    case 'bluesky':
      return 'Bluesky';
    default:
      return platform
        .split(/[_-\s]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
  }
}

const EMOJIS = ['😀','😂','😍','🔥','💯','👍','❤️','🎉','⭐','✨','🚀','😎','👏','🙌','💝','🎁','🌈','💪','🎊','🌟',
  '😢','🤔','😱','🥳','🤩','😇','🥰','💀','👀','🫡'];

/* ─── Shared shell classes ─── */
const shellIconBtn =
  'inline-flex h-9 w-9 items-center justify-center rounded-n-sm border border-n-border bg-white text-n-text-md shadow-n-xs transition hover:bg-n-hover hover:text-n-text disabled:opacity-40';
const shellToolBtn =
  'relative flex h-11 w-11 items-center justify-center rounded-n border border-n-border bg-white text-n-text-md shadow-n-xs transition hover:-translate-y-0.5 hover:bg-n-hover hover:text-n-text';
const shellToolBtnActive =
  'border-n-green bg-[rgba(34,197,94,0.08)] text-n-green shadow-[0_12px_24px_-20px_rgba(34,197,94,0.8)]';
const panelFieldClass =
  'mt-1 w-full rounded-n-sm border border-n-border bg-white px-2.5 py-1.5 text-sm text-n-text shadow-n-xs focus:outline-none focus:ring-2 focus:ring-n-green/20 focus:border-n-green';

/* ═══════════════════════════════════════════════════════════
   Helper: render final image to a canvas
   ═══════════════════════════════════════════════════════════ */
function renderToCanvas(
  canvas: HTMLCanvasElement,
  imageSrc: string,
  edit: EditState,
  paths: DrawPath[],
  texts: TextElement[],
  shapes: ShapeElement[],
  eraserPaths?: EraserPath[],
  frame?: FrameConfig,
): Promise<void> {
  return new Promise((resolve) => {
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      const filterStr = [
        `brightness(${edit.brightness / 100})`,
        `contrast(${edit.contrast / 100})`,
        `saturate(${edit.saturation / 100})`,
        edit.blur > 0 ? `blur(${edit.blur}px)` : '',
        edit.hueRotate !== 0 ? `hue-rotate(${edit.hueRotate}deg)` : '',
        edit.invert ? 'invert(1)' : '',
      ].filter(Boolean).join(' ');

      ctx.filter = filterStr || 'none';
      ctx.globalAlpha = edit.opacity / 100;

      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((edit.rotate * Math.PI) / 180);
      ctx.scale(edit.flipH ? -1 : 1, edit.flipV ? -1 : 1);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
      ctx.drawImage(img, 0, 0);
      ctx.restore();

      ctx.filter = 'none';
      ctx.globalAlpha = 1;

      // Sharpen & noise pixel effects
      applyPixelEffects(ctx, canvas.width, canvas.height, edit.sharpen, edit.noise);

      // Vignette overlay
      if (edit.vignetteIntensity > 0) {
        const amount = edit.vignetteIntensity / 100;
        const gradient = ctx.createRadialGradient(
          canvas.width / 2, canvas.height / 2, canvas.width * 0.3,
          canvas.width / 2, canvas.height / 2, canvas.width * 0.8,
        );
        gradient.addColorStop(0, 'rgba(0,0,0,0)');
        gradient.addColorStop(1, `rgba(0,0,0,${amount * 0.85})`);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Draw shapes
      shapes.forEach((s) => {
        const sx = (s.x / 100) * canvas.width;
        const sy = (s.y / 100) * canvas.height;
        const sw = (s.w / 100) * canvas.width;
        const sh = (s.h / 100) * canvas.height;
        ctx.globalAlpha = s.opacity / 100;
        ctx.strokeStyle = s.strokeColor;
        ctx.lineWidth = s.strokeWidth;
        ctx.fillStyle = s.fillColor;

        if (s.kind === 'rect') {
          ctx.beginPath();
          ctx.rect(sx, sy, sw, sh);
          ctx.fill();
          ctx.stroke();
        } else if (s.kind === 'circle') {
          ctx.beginPath();
          ctx.ellipse(sx + sw / 2, sy + sh / 2, sw / 2, sh / 2, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        } else if (s.kind === 'line') {
          ctx.beginPath();
          ctx.moveTo(sx, sy);
          ctx.lineTo(sx + sw, sy + sh);
          ctx.stroke();
        } else if (s.kind === 'arrow') {
          const headLen = Math.max(12, s.strokeWidth * 4);
          const angle = Math.atan2(sh, sw);
          ctx.beginPath();
          ctx.moveTo(sx, sy);
          ctx.lineTo(sx + sw, sy + sh);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(sx + sw, sy + sh);
          ctx.lineTo(
            sx + sw - headLen * Math.cos(angle - Math.PI / 6),
            sy + sh - headLen * Math.sin(angle - Math.PI / 6),
          );
          ctx.moveTo(sx + sw, sy + sh);
          ctx.lineTo(
            sx + sw - headLen * Math.cos(angle + Math.PI / 6),
            sy + sh - headLen * Math.sin(angle + Math.PI / 6),
          );
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      });

      // Draw freehand paths
      paths.forEach((p) => {
        if (p.points.length < 2) return;
        ctx.beginPath();
        ctx.strokeStyle = p.color;
        ctx.lineWidth = p.width;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.moveTo((p.points[0].x / 100) * canvas.width, (p.points[0].y / 100) * canvas.height);
        p.points.slice(1).forEach((pt) => {
          ctx.lineTo((pt.x / 100) * canvas.width, (pt.y / 100) * canvas.height);
        });
        ctx.stroke();
      });

      // Eraser paths (destination-out)
      if (eraserPaths && eraserPaths.length > 0) {
        ctx.globalCompositeOperation = 'destination-out';
        eraserPaths.forEach((p) => {
          if (p.points.length < 2) return;
          ctx.beginPath();
          ctx.lineWidth = p.width;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.moveTo((p.points[0].x / 100) * canvas.width, (p.points[0].y / 100) * canvas.height);
          p.points.slice(1).forEach((pt) => {
            ctx.lineTo((pt.x / 100) * canvas.width, (pt.y / 100) * canvas.height);
          });
          ctx.stroke();
        });
        ctx.globalCompositeOperation = 'source-over';
      }

      // Draw text layers (with stroke + shadow)
      texts.forEach((t) => {
        const x = (t.x / 100) * canvas.width;
        const y = (t.y / 100) * canvas.height;
        ctx.font = `${t.bold ? 'bold ' : ''}${t.italic ? 'italic ' : ''}${t.fontSize}px "${t.fontFamily || 'Arial'}", Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = t.shadowColor || 'transparent';
        ctx.shadowBlur = t.shadowBlur ?? 0;
        ctx.shadowOffsetX = t.shadowOffsetX ?? 0;
        ctx.shadowOffsetY = t.shadowOffsetY ?? 0;
        if ((t.strokeWidth ?? 0) > 0) {
          ctx.strokeStyle = t.strokeColor || '#000000';
          ctx.lineWidth = t.strokeWidth ?? 0;
          ctx.lineJoin = 'round';
          ctx.strokeText(t.text, x, y);
        }
        ctx.fillStyle = t.color;
        ctx.fillText(t.text, x, y);
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      });

      // Frame / border (post-process)
      if (frame?.enabled && (frame.width ?? 0) > 0) {
        const fw = frame.width;
        ctx.globalAlpha = (frame.opacity ?? 100) / 100;
        if (frame.style === 'solid') {
          ctx.fillStyle = frame.color1;
        } else if (frame.style === 'gradient-lr') {
          const g = ctx.createLinearGradient(0, 0, canvas.width, 0);
          g.addColorStop(0, frame.color1); g.addColorStop(1, frame.color2);
          ctx.fillStyle = g;
        } else if (frame.style === 'gradient-tb') {
          const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
          g.addColorStop(0, frame.color1); g.addColorStop(1, frame.color2);
          ctx.fillStyle = g;
        } else if (frame.style === 'double') {
          // outer
          ctx.fillStyle = frame.color1;
          ctx.fillRect(0, 0, canvas.width, fw);
          ctx.fillRect(0, canvas.height - fw, canvas.width, fw);
          ctx.fillRect(0, 0, fw, canvas.height);
          ctx.fillRect(canvas.width - fw, 0, fw, canvas.height);
          // inner (half-width)
          const iw = Math.max(1, Math.round(fw / 3));
          const gap = Math.max(1, Math.round(fw / 5));
          ctx.fillStyle = frame.color2;
          ctx.fillRect(fw + gap, fw + gap, canvas.width - (fw + gap) * 2, iw);
          ctx.fillRect(fw + gap, canvas.height - fw - gap - iw, canvas.width - (fw + gap) * 2, iw);
          ctx.fillRect(fw + gap, fw + gap, iw, canvas.height - (fw + gap) * 2);
          ctx.fillRect(canvas.width - fw - gap - iw, fw + gap, iw, canvas.height - (fw + gap) * 2);
          ctx.globalAlpha = 1;
          resolve();
          return;
        }
        // top, bottom, left, right bands
        ctx.fillRect(0, 0, canvas.width, fw);
        ctx.fillRect(0, canvas.height - fw, canvas.width, fw);
        ctx.fillRect(0, 0, fw, canvas.height);
        ctx.fillRect(canvas.width - fw, 0, fw, canvas.height);
        ctx.globalAlpha = 1;
      }

      resolve();
    };
    img.src = imageSrc;
  });
}

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
const ImageEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const pc = theme.hex?.primary || '#2383e2';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editorReturnTo = searchParams.get('returnTo');
  const editorSource = searchParams.get('source');
  const editorContentType = searchParams.get('content_type') || '';
  const selectedPlatformsParam = searchParams.get('selected_platforms') || '';
  const editorSlotIndex = searchParams.get('slot_index');
  const editorBaseMediaId = searchParams.get('base_media_id');
  const editorDraftKey = searchParams.get('draft_key');

  const selectedPlatformLabels = useMemo(
    () =>
      selectedPlatformsParam
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .map(formatPlatformName),
    [selectedPlatformsParam]
  );

  const editorContentTypeLabel = useMemo(() => {
    switch (editorContentType) {
      case 'story':
        return t('editor_content_type_story', 'Story');
      case 'reel':
        return t('editor_content_type_reel', 'Reel');
      case 'carousel':
        return t('editor_content_type_carousel', 'Carousel');
      case 'short':
        return t('editor_content_type_short', 'Short');
      case 'igtv':
        return t('editor_content_type_igtv', 'IGTV');
      case 'post':
        return t('editor_content_type_post', 'Post');
      default:
        return '';
    }
  }, [editorContentType, t]);

  const editorContextHeading = useMemo(() => {
    if (editorSource === 'studio') {
      return t('editor_context_source_studio', 'AI Studio draft');
    }
    if (editorSource === 'compose') {
      return t('editor_context_source_compose', 'Composer draft');
    }
    return t('editor_context_source_library', 'Media Library asset');
  }, [editorSource, t]);

  const returnsToDraft =
    Boolean(editorReturnTo) &&
    (editorSource === 'compose' || editorSource === 'studio');
  const [draftSaveMode, setDraftSaveMode] = useState<
    'save_only' | 'replace_current_media' | 'keep_original_use_for_selected_platforms'
  >(returnsToDraft ? 'replace_current_media' : 'save_only');

  const editorContextSummary = useMemo(() => {
    if (returnsToDraft) {
      if (selectedPlatformLabels.length > 0) {
        return t(
          'editor_context_summary_platforms',
          'Save once and send this edited asset back to your current draft for {{platforms}}.',
          { platforms: selectedPlatformLabels.join(', ') }
        );
      }
      if (editorContentTypeLabel) {
        return t(
          'editor_context_summary_draft',
          'Save once and send this edited asset back to your current {{contentType}} draft.',
          { contentType: editorContentTypeLabel }
        );
      }
      return t(
        'editor_context_summary_default',
        'Save once and send this edited asset back to the draft you came from.'
      );
    }
    return t(
      'editor_context_summary_library',
      'Save polished assets back to your media library so they are ready to reuse later.'
    );
  }, [editorContentTypeLabel, returnsToDraft, selectedPlatformLabels, t]);

  const saveActionLabel = useMemo(() => {
    if (!editorReturnTo) {
      return t('editor_save_to_library', 'Save to Library');
    }
    if (!returnsToDraft) {
      return t('editor_save_and_return', 'Save and return');
    }
    if (draftSaveMode === 'replace_current_media') {
      return t('editor_save_asset_and_replace', 'Save asset + replace draft');
    }
    if (draftSaveMode === 'keep_original_use_for_selected_platforms') {
      return t(
        'editor_save_asset_and_variant',
        'Save asset + use for selected platforms',
      );
    }
    return t('editor_save_asset_only', 'Save as asset only');
  }, [draftSaveMode, editorReturnTo, returnsToDraft, t]);

  const buildReturnUrl = useCallback(
    (mediaId?: string) => {
      if (!editorReturnTo) {
        return null;
      }

      const queryIndex = editorReturnTo.indexOf('?');
      const pathname =
        queryIndex >= 0 ? editorReturnTo.slice(0, queryIndex) : editorReturnTo;
      const queryString =
        queryIndex >= 0 ? editorReturnTo.slice(queryIndex + 1) : '';
      const params = new URLSearchParams(queryString);

      if (mediaId && returnsToDraft) {
        params.set('editor_media_id', mediaId);
        params.set('editor_apply_mode', draftSaveMode);
        if (editorSlotIndex) {
          params.set('editor_slot_index', editorSlotIndex);
        }
        if (editorBaseMediaId) {
          params.set('editor_base_media_id', editorBaseMediaId);
        }
        if (selectedPlatformsParam) {
          params.set('editor_target_platforms', selectedPlatformsParam);
        }
        if (editorDraftKey) {
          params.set('draft_key', editorDraftKey);
        }
      } else if (mediaId) {
        params.set('newMediaId', mediaId);
      }

      const serialized = params.toString();
      return serialized ? `${pathname}?${serialized}` : pathname;
    },
    [
      draftSaveMode,
      editorBaseMediaId,
      editorDraftKey,
      editorReturnTo,
      editorSlotIndex,
      returnsToDraft,
      selectedPlatformsParam,
    ]
  );

  const handleBack = useCallback(() => {
    if (editorReturnTo) {
      navigate(editorReturnTo);
      return;
    }
    navigate(-1);
  }, [editorReturnTo, navigate]);

  // Core state
  const [image, setImage] = useState<string | null>(null);
  const [originalDims, setOriginalDims] = useState({ w: 0, h: 0 });
  const [edit, setEdit] = useState<EditState>(defaultEdit);
  const [history, setHistory] = useState<EditState[]>([defaultEdit]);
  const [histIdx, setHistIdx] = useState(0);
  const [activeTool, setActiveTool] = useState<ToolType>(null);
  const [activeFilter, setActiveFilter] = useState<FilterPresetName>('none');
  const [bgRemoving, setBgRemoving] = useState(false);

  // Eraser state
  const [eraserPaths, setEraserPaths] = useState<EraserPath[]>([]);
  const [eraserWidth, setEraserWidth] = useState(20);
  const [isErasing, setIsErasing] = useState(false);

  // Frame state
  const [frame, setFrame] = useState<FrameConfig>({
    enabled: false, style: 'solid',
    color1: '#ffffff', color2: '#000000',
    width: 40, opacity: 100,
  });

  // Collage state
  const [collageLayout, setCollageLayout] = useState<CollageLayout>('1+1');
  const [collageSlots, setCollageSlots] = useState<CollageSlot[]>(
    Array.from({ length: 4 }, (_, i) => ({ id: `slot-${i}`, src: null, label: `Image ${i + 1}` }))
  );
  const [collagePickingSlot, setCollagePickingSlot] = useState<number | null>(null);

  // AI caption suggestion state (Tier 3 #12)
  // savedMediaId is the media library id for the currently-flattened image,
  // cached so repeated caption requests don't re-upload the same bytes. It's
  // cleared whenever the canvas content changes (see useEffect further down).
  const [savedMediaId, setSavedMediaId] = useState<string | null>(null);
  const [captions, setCaptions] = useState<string[]>([]);
  const [altText, setAltText] = useState<string>('');
  const [copiedCaptionIdx, setCopiedCaptionIdx] = useState<number | null>(null);

  // Text state
  const [texts, setTexts] = useState<TextElement[]>([]);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [draggingText, setDraggingText] = useState<string | null>(null);

  // Shape state
  const [shapes, setShapes] = useState<ShapeElement[]>([]);
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
  const [shapeTool, setShapeTool] = useState<ShapeKind>('rect');
  const [shapeFill, setShapeFill] = useState('#ffffff');
  const [shapeStroke, setShapeStroke] = useState('#000000');
  const [shapeStrokeW, setShapeStrokeW] = useState(2);
  const [shapeOpacity, setShapeOpacity] = useState(80);
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const shapeStartRef = useRef<{ x: number; y: number } | null>(null);
  const [draggingShapeId, setDraggingShapeId] = useState<string | null>(null);
  const shapeDragStartRef = useRef<{ mx: number; my: number; sx: number; sy: number } | null>(null);

  // Draw state
  const [paths, setPaths] = useState<DrawPath[]>([]);
  const [drawColor, setDrawColor] = useState(pc);
  const [drawWidth, setDrawWidth] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [uploadBytes, setUploadBytes] = useState<{ loaded: number; total: number }>({ loaded: 0, total: 0 });

  // Crop state
  const [crop, setCrop] = useState<CropRegion>({ x: 10, y: 10, w: 80, h: 80 });
  const [isCropping, setIsCropping] = useState(false);
  const [cropRatio, setCropRatio] = useState(0);

  // Resize state
  const [lockAR, setLockAR] = useState(true);

  // Export
  const [exportFmt, setExportFmt] = useState<'png' | 'jpg' | 'webp'>('png');
  const [exportQuality, setExportQuality] = useState(92);

  // Media picker modal
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [selectedBrandKitId, setSelectedBrandKitId] = useState<string | null>(null);

  // Refs
  const fileRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgAreaRef = useRef<HTMLDivElement>(null);

  // Accessibility IDs
  const widthId = React.useId();
  const heightId = React.useId();
  const drawWidthId = React.useId();
  const drawColorId = React.useId();
  const textSizeId = React.useId();
  const textColorId = React.useId();
  const textFontId = React.useId();
  const invertId = React.useId();
  const vignetteId = React.useId();
  const sharpenId = React.useId();
  const noiseId = React.useId();

  // Inject Google Fonts once
  useEffect(() => { ensureGoogleFonts(); }, []);

  const { data: brandKitResponse } = useQuery({
    queryKey: ['editor-brand-kits'],
    queryFn: async () => {
      const response = await aiImagesAPI.listBrandKits();
      return (response.data?.brand_kits || []) as EditorBrandKit[];
    },
    staleTime: 60_000,
  });

  const brandKits = useMemo(() => brandKitResponse || [], [brandKitResponse]);
  const activeBrandKit = useMemo(
    () => brandKits.find((kit) => kit.id === selectedBrandKitId) || null,
    [brandKits, selectedBrandKitId]
  );
  const brandPrimaryColor = activeBrandKit?.primary_color || '#ffffff';
  const brandSecondaryColor = activeBrandKit?.secondary_color || pc;
  const brandAccentColor = activeBrandKit?.accent_colors?.[0] || pc;
  const brandPrimaryFont = activeBrandKit?.primary_font || 'Inter';
  const brandSecondaryFont = activeBrandKit?.secondary_font || 'Playfair Display';

  useEffect(() => {
    if (selectedBrandKitId || brandKits.length === 0) {
      return;
    }
    const defaultKit = brandKits.find((kit) => kit.is_default) || brandKits[0];
    setSelectedBrandKitId(defaultKit.id);
  }, [brandKits, selectedBrandKitId]);

  /* ─── Load from URL ─── */
  const applyLoadedImage = useCallback((img: HTMLImageElement) => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = img.width;
    tempCanvas.height = img.height;
    const ctx = tempCanvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    const dataUrl = tempCanvas.toDataURL('image/png');
    setOriginalDims({ w: img.width, h: img.height });
    const fresh = { ...defaultEdit, width: img.width, height: img.height };
    setEdit(fresh);
    setHistory([fresh]);
    setHistIdx(0);
    setTexts([]);
    setPaths([]);
    setShapes([]);
    setActiveFilter('none');
    setCrop({ x: 10, y: 10, w: 80, h: 80 });
    setImage(dataUrl);
  }, []);

  const loadFromBlobUrl = useCallback((blobUrl: string, sourceLabel: string) => {
    const img = new Image();
    img.onload = () => {
      try {
        applyLoadedImage(img);
      } catch (err) {
        console.error('[ImageEditor] applyLoadedImage threw for', sourceLabel, err);
        toast.error(t('editor_load_failed_decode', 'Image loaded but could not be decoded'));
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      console.error('[ImageEditor] <img> onerror for', sourceLabel, '— response was not a valid image');
      toast.error(t('editor_load_failed_not_image', 'Response was not a valid image'));
    };
    img.src = blobUrl;
  }, [applyLoadedImage, t]);

  /**
   * Fetch an image URL as a Blob with three strategies, in order:
   *   1. Short-circuit — data: URLs are decoded directly by fetch().
   *   2. Direct CORS fetch — works for S3/CDN URLs that send proper headers.
   *   3. Backend proxy — /api/media/proxy?url=… for whitelisted storage hosts.
   *
   * Each failure mode produces a specific toast + console.error so the real
   * cause is visible to both users and developers.
   */
  const fetchImageBlob = useCallback(async (url: string): Promise<Blob> => {
    // 1) data: URL short-circuit — fetch handles these natively without CORS.
    if (url.startsWith('data:')) {
      const resp = await fetch(url);
      return resp.blob();
    }

    // 2) Direct CORS fetch
    let directErr: unknown = null;
    try {
      const r = await fetch(url, { mode: 'cors' });
      if (r.ok) {
        return await r.blob();
      }
      directErr = new Error(`HTTP ${r.status}`);
      console.warn('[ImageEditor] direct fetch failed with status', r.status, 'for', url);
    } catch (err) {
      directErr = err;
      console.warn('[ImageEditor] direct fetch threw (likely CORS or network) for', url, err);
    }

    // 3) Backend proxy fallback
    const apiBase = import.meta.env.VITE_API_URL || '';
    const proxyUrl = `${apiBase}/api/media/proxy?url=${encodeURIComponent(url)}`;
    try {
      const r = await fetch(proxyUrl);
      if (r.ok) {
        return await r.blob();
      }
      // Try to extract the backend's detail message (e.g. "URL not from
      // allowed storage domain") so the user can act on it.
      let detail = '';
      try {
        const body = await r.json();
        detail = typeof body?.detail === 'string' ? body.detail : '';
      } catch {
        // Response wasn't JSON; ignore.
      }
      console.error('[ImageEditor] proxy fetch failed', r.status, detail, 'for', url);
      if (r.status === 400 && detail.toLowerCase().includes('allowed')) {
        throw new Error(
          t(
            'editor_load_failed_proxy_host',
            'This image host is not allowed by the proxy. Contact your admin to whitelist it.',
          ),
        );
      }
      if (r.status === 401 || r.status === 403) {
        throw new Error(
          t('editor_load_failed_auth', 'Not authorised to load this image'),
        );
      }
      if (r.status >= 500) {
        throw new Error(
          t('editor_load_failed_proxy_5xx', 'Image proxy is unavailable. Try again in a moment.'),
        );
      }
      throw new Error(detail || t('editor_load_failed', 'Failed to load image from URL'));
    } catch (err) {
      // If we already have a typed Error from the block above, rethrow it.
      if (err instanceof Error && err.message) {
        throw err;
      }
      console.error('[ImageEditor] proxy fetch threw', err, 'after direct error', directErr);
      throw new Error(
        t('editor_load_failed_network', 'Network error while loading image'),
      );
    }
  }, [t]);

  const loadImageFromUrl = useCallback((url: string) => {
    if (!url || url === 'undefined' || url === 'null') {
      toast.error(t('editor_load_failed_invalid_url', 'Image URL is missing or invalid'));
      return;
    }
    fetchImageBlob(url)
      .then((blob) => loadFromBlobUrl(URL.createObjectURL(blob), url))
      .catch((err: unknown) => {
        toast.error(getErrorMessage(err, t('editor_load_failed', 'Failed to load image from URL')));
      });
  }, [t, loadFromBlobUrl, fetchImageBlob]);

  const loadImageByMediaId = useCallback((mediaId: string) => {
    if (!mediaId || mediaId === 'undefined' || mediaId === 'null') {
      toast.error(t('editor_load_failed_invalid_id', 'Media id is missing or invalid'));
      return;
    }
    const apiBase = import.meta.env.VITE_API_URL || '';
    const token = localStorage.getItem('access_token');
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    fetch(`${apiBase}/api/media/${mediaId}/content`, { headers })
      .then(async (r) => {
        if (!r.ok) {
          console.error('[ImageEditor] /api/media/:id/content failed with', r.status, 'for', mediaId);
          if (r.status === 404) {
            throw new Error(t('editor_load_failed_not_found', 'Image not found in your library'));
          }
          if (r.status === 401 || r.status === 403) {
            throw new Error(t('editor_load_failed_auth', 'Not authorised to load this image'));
          }
          throw new Error(t('editor_load_failed_content_api', 'Could not resolve image URL from backend'));
        }
        return r.json() as Promise<{ url: string }>;
      })
      .then((data) => {
        if (!data?.url) {
          throw new Error(t('editor_load_failed_content_api', 'Could not resolve image URL from backend'));
        }
        return fetchImageBlob(data.url);
      })
      .then((blob) => loadFromBlobUrl(URL.createObjectURL(blob), `mediaId=${mediaId}`))
      .catch((err: unknown) => {
        toast.error(getErrorMessage(err, t('editor_load_failed', 'Failed to load image from URL')));
      });
  }, [t, loadFromBlobUrl, fetchImageBlob]);

  useEffect(() => {
    const mediaId = searchParams.get('mediaId');
    if (mediaId) { loadImageByMediaId(mediaId); return; }
    const imgUrl = searchParams.get('url') || searchParams.get('src');
    if (imgUrl) loadImageFromUrl(imgUrl);
  }, [searchParams, loadImageByMediaId, loadImageFromUrl]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if (mod && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedTextId && activeTool === 'text') deleteText(selectedTextId);
        if (selectedShapeId && activeTool === 'shapes') deleteShape(selectedShapeId);
      }
      if (mod && e.key === 's') { e.preventDefault(); if (image) exportImage(); }
      if (mod && e.key === '=') { e.preventDefault(); pushEdit({ zoom: Math.min(3, edit.zoom + 0.1) }); }
      if (mod && e.key === '-') { e.preventDefault(); pushEdit({ zoom: Math.max(0.25, edit.zoom - 0.1) }); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  /* ─── Load from Media Library ─── */
  const handleMediaPick = (item: { id?: string; original_url: string; media_type: string; filename: string }) => {
    // Branch 1: collage slot fill — if a slot is actively being picked, route
    // the selection into the slot instead of replacing the main canvas.
    if (collagePickingSlot !== null) {
      const slotIdx = collagePickingSlot;
      const applyToSlot = (src: string) => {
        setCollageSlots((prev) => prev.map((s, i) => (i === slotIdx ? { ...s, src } : s)));
        setCollagePickingSlot(null);
        toast.success(t('editor_collage_slot_added', 'Image added to collage slot'));
      };
      if (item.media_type === 'video') {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.preload = 'auto';
        video.onloadeddata = () => { video.currentTime = 0.1; };
        video.onseeked = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(video, 0, 0);
              applyToSlot(canvas.toDataURL('image/png'));
            }
          } catch {
            setCollagePickingSlot(null);
            toast.error(t('editor_video_frame_failed', 'Could not extract video frame'));
          }
        };
        video.onerror = () => {
          setCollagePickingSlot(null);
          toast.error(t('editor_video_load_failed', 'Failed to load video'));
        };
        video.src = item.original_url;
      } else {
        applyToSlot(item.original_url);
      }
      return;
    }

    // Branch 2: default — load into the main canvas.
    if (item.media_type === 'video') {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'auto';
      video.onloadeddata = () => { video.currentTime = 0.1; };
      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0);
            const dataUrl = canvas.toDataURL('image/png');
            setOriginalDims({ w: video.videoWidth, h: video.videoHeight });
            const fresh = { ...defaultEdit, width: video.videoWidth, height: video.videoHeight };
            setEdit(fresh); setHistory([fresh]); setHistIdx(0);
            setTexts([]); setPaths([]); setShapes([]);
            setActiveFilter('none'); setCrop({ x: 10, y: 10, w: 80, h: 80 });
            setImage(dataUrl);
            toast.success(t('editor_video_frame_loaded', 'Video frame loaded for editing'));
          }
        } catch { toast.error(t('editor_video_frame_failed', 'Could not extract video frame')); }
      };
      video.onerror = () => toast.error(t('editor_video_load_failed', 'Failed to load video'));
      video.src = item.original_url;
    } else if (item.id) {
      // Prefer the authenticated /api/media/:id/content path — it returns a
      // fresh presigned URL every time, which sidesteps the stale-URL and
      // stale-token problems we'd hit with whatever `original_url` was cached
      // by the picker. Falls back to direct URL only when the picker omitted
      // the id (e.g., an externally-sourced item).
      loadImageByMediaId(item.id);
    } else {
      loadImageFromUrl(item.original_url);
    }
  };

  /* ─── Helpers ─── */
  const pushEdit = useCallback((updates: Partial<EditState>) => {
    setEdit((prev) => {
      const next = { ...prev, ...updates };
      setHistory((h) => {
        const trimmed = h.slice(0, histIdx + 1);
        trimmed.push(next);
        setHistIdx(trimmed.length - 1);
        return trimmed;
      });
      return next;
    });
  }, [histIdx]);

  const undo = () => { if (histIdx > 0) { setHistIdx(histIdx - 1); setEdit(history[histIdx - 1]); } };
  const redo = () => { if (histIdx < history.length - 1) { setHistIdx(histIdx + 1); setEdit(history[histIdx + 1]); } };

  const cssFilter = `brightness(${edit.brightness}%) contrast(${edit.contrast}%) saturate(${edit.saturation}%) blur(${edit.blur}px) hue-rotate(${edit.hueRotate}deg) opacity(${edit.opacity}%)${edit.invert ? ' invert(1)' : ''}`;
  const cssTransform = `rotate(${edit.rotate}deg) scaleX(${edit.flipH ? -1 : 1}) scaleY(${edit.flipV ? -1 : 1}) scale(${edit.zoom})`;

  /* ─── Image Upload ─── */
  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error(t('editor_not_image', 'Not an image')); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        setOriginalDims({ w: img.width, h: img.height });
        const fresh = { ...defaultEdit, width: img.width, height: img.height };
        setEdit(fresh); setHistory([fresh]); setHistIdx(0);
        setTexts([]); setPaths([]); setShapes([]);
        setActiveFilter('none'); setCrop({ x: 10, y: 10, w: 80, h: 80 });
      };
      img.src = src;
      setImage(src);
    };
    reader.readAsDataURL(file);
  };

  /* ─── Background Removal (Tier 1 #2) ─── */
  const handleBgRemove = async () => {
    if (!image) return;
    setBgRemoving(true);
    try {
      // Dynamic import so the WASM is only loaded on demand
      const { removeBackground } = await import('@imgly/background-removal');
      const blob = await fetch(image).then((r) => r.blob());
      const resultBlob = await removeBackground(blob, {
        model: 'isnet_quint8', // smallest/fastest quantised model
        output: { format: 'image/png', quality: 1 },
      });
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target!.result as string);
        reader.readAsDataURL(resultBlob);
      });
      setImage(dataUrl);
      toast.success(t('editor_bg_removed', 'Background removed!'));
    } catch (err) {
      console.error(err);
      toast.error(t('editor_bg_remove_failed', 'Background removal failed'));
    } finally {
      setBgRemoving(false);
    }
  };

  /* ─── Crop Apply ─── */
  const applyCrop = () => {
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    img.onload = () => {
      const sx = (crop.x / 100) * img.width;
      const sy = (crop.y / 100) * img.height;
      const sw = (crop.w / 100) * img.width;
      const sh = (crop.h / 100) * img.height;
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      const cropped = canvas.toDataURL('image/png');
      setImage(cropped);
      setOriginalDims({ w: canvas.width, h: canvas.height });
      pushEdit({ width: canvas.width, height: canvas.height });
      setCrop({ x: 10, y: 10, w: 80, h: 80 });
      setIsCropping(false);
      toast.success(t('editor_crop_applied', 'Crop applied'));
    };
    img.src = image;
  };

  /* ─── Resize Apply ─── */
  const applyResize = () => {
    if (!image || !canvasRef.current || edit.width <= 0 || edit.height <= 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    img.onload = () => {
      canvas.width = edit.width;
      canvas.height = edit.height;
      ctx.drawImage(img, 0, 0, edit.width, edit.height);
      const resized = canvas.toDataURL('image/png');
      setImage(resized);
      setOriginalDims({ w: edit.width, h: edit.height });
      toast.success(t('editor_resized', `Resized to ${edit.width}×${edit.height}`));
    };
    img.src = image;
  };

  /* ─── Export (download) ─── */
  /* ─── Collage flatten ─── */
  const flattenCollage = () => {
    const layoutSlotCounts: Record<CollageLayout, number> = { '1+1': 2, '2+1': 3, 'grid-4': 4 };
    const count = layoutSlotCounts[collageLayout];
    const filled = collageSlots.filter((s) => s.src).slice(0, count);
    if (filled.length < 2) { toast.error('Add at least 2 images to create a collage'); return; }

    const OUT_W = 1080;
    const OUT_H = collageLayout === '1+1' ? 540 : collageLayout === '2+1' ? 1080 : 1080;
    const flatCanvas = document.createElement('canvas');
    flatCanvas.width = OUT_W; flatCanvas.height = OUT_H;
    const ctx = flatCanvas.getContext('2d')!;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, OUT_W, OUT_H);
    const GAP = 4;

    const loadImg = (src: string): Promise<HTMLImageElement> =>
      new Promise((res, rej) => {
        const i = new Image();
        // Enable CORS for remote URLs so the composed canvas remains exportable.
        // data: URLs are unaffected by this flag.
        if (!src.startsWith('data:')) {
          i.crossOrigin = 'anonymous';
        }
        i.onload = () => res(i);
        i.onerror = () => rej(new Error('Failed to load collage image'));
        i.src = src;
      });

    const slots: { x: number; y: number; w: number; h: number }[] =
      collageLayout === '1+1'
        ? [{ x: 0, y: 0, w: OUT_W / 2 - GAP / 2, h: OUT_H }, { x: OUT_W / 2 + GAP / 2, y: 0, w: OUT_W / 2 - GAP / 2, h: OUT_H }]
        : collageLayout === '2+1'
        ? [{ x: 0, y: 0, w: OUT_W / 2 - GAP / 2, h: OUT_H / 2 - GAP / 2 },
           { x: 0, y: OUT_H / 2 + GAP / 2, w: OUT_W / 2 - GAP / 2, h: OUT_H / 2 - GAP / 2 },
           { x: OUT_W / 2 + GAP / 2, y: 0, w: OUT_W / 2 - GAP / 2, h: OUT_H }]
        : [{ x: 0, y: 0, w: OUT_W / 2 - GAP / 2, h: OUT_H / 2 - GAP / 2 },
           { x: OUT_W / 2 + GAP / 2, y: 0, w: OUT_W / 2 - GAP / 2, h: OUT_H / 2 - GAP / 2 },
           { x: 0, y: OUT_H / 2 + GAP / 2, w: OUT_W / 2 - GAP / 2, h: OUT_H / 2 - GAP / 2 },
           { x: OUT_W / 2 + GAP / 2, y: OUT_H / 2 + GAP / 2, w: OUT_W / 2 - GAP / 2, h: OUT_H / 2 - GAP / 2 }];

    Promise.all(filled.map((s) => loadImg(s.src!)))
      .then((imgs) => {
        imgs.forEach((img, i) => {
          if (!slots[i]) {
            return;
          }
          const { x, y, w, h } = slots[i];
          // cover-fit
          const scale = Math.max(w / img.width, h / img.height);
          const sw = img.width * scale, sh = img.height * scale;
          ctx.drawImage(img, x + (w - sw) / 2, y + (h - sh) / 2, sw, sh);
        });
        try {
          const dataUrl = flatCanvas.toDataURL('image/png');
          const fresh = { ...defaultEdit, width: OUT_W, height: OUT_H };
          setEdit(fresh); setHistory([fresh]); setHistIdx(0);
          setTexts([]); setPaths([]); setShapes([]); setEraserPaths([]);
          setOriginalDims({ w: OUT_W, h: OUT_H });
          setImage(dataUrl);
          setActiveTool(null);
          toast.success(t('editor_collage_created', 'Collage created!'));
        } catch {
          toast.error(t('editor_collage_cors_failed', 'Could not export collage (one of the images blocked CORS)'));
        }
      })
      .catch(() => {
        toast.error(t('editor_collage_load_failed', 'Failed to load one of the collage images'));
      });
  };

  const exportImage = async () => {
    if (!image || !canvasRef.current) { toast.error(t('editor_no_image', 'No image')); return; }
    await renderToCanvas(canvasRef.current, image, edit, paths, texts, shapes, eraserPaths, frame);
    const mime = exportFmt === 'jpg' ? 'image/jpeg' : exportFmt === 'webp' ? 'image/webp' : 'image/png';
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `edited.${exportFmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t('editor_exported', 'Exported!'));
    }, mime, exportFmt === 'jpg' ? exportQuality / 100 : 1);
  };

  /* ─── Save to Media Library ─── */
  const saveToLibraryMutation = useMutation({
    mutationFn: async () => {
      if (!image || !canvasRef.current) throw new Error('No image');
      await renderToCanvas(canvasRef.current, image, edit, paths, texts, shapes, eraserPaths, frame);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvasRef.current!.toBlob((b) => { if (b) resolve(b); else reject(new Error('Failed to create blob')); }, 'image/png');
      });
      const file = new File([blob], `edited-${Date.now()}.png`, { type: 'image/png' });
      setUploadBytes({ loaded: 0, total: file.size });
      const resp = await mediaAPI.directUpload(file, (loaded, total) => {
        setUploadBytes({ loaded, total });
      });
      return resp.data;
    },
    onSuccess: (data) => {
      const mediaId: string | undefined = data.media_id || data.id;
      if (mediaId) {
        setSavedMediaId(mediaId);
      }
      if (editorReturnTo) {
        toast.success(
          returnsToDraft
            ? (
              draftSaveMode === 'replace_current_media'
                ? t(
                    'editor_saved_and_replacing',
                    'Saved as an asset. Updating the current draft media…',
                  )
                : draftSaveMode === 'keep_original_use_for_selected_platforms'
                  ? t(
                      'editor_saved_and_varianting',
                      'Saved as an asset. Applying this version to the selected platforms…',
                    )
                  : t(
                      'editor_saved_and_returning',
                      'Saved as an asset. Returning to your draft…',
                    )
            )
            : t('editor_saved_and_returning', 'Saved. Returning to your draft…')
        );
        const nextUrl = buildReturnUrl(mediaId);
        if (nextUrl) {
          navigate(nextUrl);
          return;
        }
      }
      toast.success(t('editor_saved_to_library', 'Saved to media library!'));
    },
    onError: (error: unknown) =>
      toast.error(
        getErrorMessage(error, t('editor_save_failed', 'Failed to save to library'))
      ),
  });

  /* ─── AI Caption Suggestions (Tier 3 #12) ─── */
  // Invalidate cached media_id + captions when the rendered pixels change,
  // so the next "Suggest Captions" click re-uploads the current version.
  useEffect(() => {
    if (savedMediaId !== null) {
      setSavedMediaId(null);
    }
    if (captions.length > 0 || altText !== '') {
      setCaptions([]);
      setAltText('');
    }
    // Intentionally only watching content-affecting state; savedMediaId/captions
    // must NOT be dependencies or we'd loop on our own setState calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, edit, texts, paths, shapes, eraserPaths, frame]);

  const suggestCaptionsMutation = useMutation({
    mutationFn: async () => {
      if (!image || !canvasRef.current) {
        throw new Error('No image');
      }
      // Reuse a cached media_id if the canvas hasn't changed since the last save.
      let mediaId = savedMediaId;
      if (!mediaId) {
        await renderToCanvas(
          canvasRef.current, image, edit, paths, texts, shapes, eraserPaths, frame,
        );
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvasRef.current!.toBlob(
            (b) => { if (b) { resolve(b); } else { reject(new Error('Failed to create blob')); } },
            'image/png',
          );
        });
        const file = new File([blob], `edited-${Date.now()}.png`, { type: 'image/png' });
        setUploadBytes({ loaded: 0, total: file.size });
        const uploadResp = await mediaAPI.directUpload(file, (loaded, total) => {
          setUploadBytes({ loaded, total });
        });
        const uploadedId: string | undefined = uploadResp.data.media_id || uploadResp.data.id;
        if (!uploadedId) {
          throw new Error('Upload did not return a media id');
        }
        mediaId = uploadedId;
        setSavedMediaId(uploadedId);
      }
      const describeResp = await aiAPI.describeImage(mediaId);
      return describeResp.data as {
        alt_text?: string;
        caption_suggestions?: string[];
        detected_objects?: string[];
      };
    },
    onSuccess: (data) => {
      const suggestions = Array.isArray(data.caption_suggestions) ? data.caption_suggestions : [];
      setCaptions(suggestions);
      setAltText(typeof data.alt_text === 'string' ? data.alt_text : '');
      if (suggestions.length === 0) {
        toast(t('editor_captions_empty', 'No caption suggestions returned'));
      } else {
        toast.success(
          t('editor_captions_generated', '{{count}} caption suggestions ready', {
            count: suggestions.length,
          }),
        );
      }
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, t('editor_captions_failed', 'Failed to generate captions')),
      );
    },
  });

  const handleCopyCaption = useCallback(async (text: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCaptionIdx(idx);
      setTimeout(() => setCopiedCaptionIdx((cur) => (cur === idx ? null : cur)), 1500);
    } catch {
      toast.error(t('editor_captions_copy_failed', 'Could not copy to clipboard'));
    }
  }, [t]);

  const handleUseCaptionInStudio = useCallback((caption: string) => {
    // AG-UI reads ?prompt= and auto-sends it on mount (AgentChat.tsx:98–110).
    const seed = t(
      'editor_captions_studio_seed',
      'Help me turn this caption into a full post: "{{caption}}"',
      { caption },
    );
    navigate(`/agent?prompt=${encodeURIComponent(seed)}`);
  }, [navigate, t]);

  /* ─── Text helpers ─── */
  const addText = () => {
    const el: TextElement = {
      id: `t-${Date.now()}`, text: 'Text', x: 50, y: 50,
      fontSize: 32,
      color: brandPrimaryColor,
      fontFamily: brandPrimaryFont,
      bold: false,
      italic: false,
      shadowBlur: 0, shadowColor: 'rgba(0,0,0,0.8)', shadowOffsetX: 1, shadowOffsetY: 1,
      strokeColor: '#000000', strokeWidth: 0,
    };
    setTexts((prev) => [...prev, el]);
    setSelectedTextId(el.id);
  };
  const updateText = (id: string, u: Partial<TextElement>) =>
    setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, ...u } : t)));
  const deleteText = (id: string) => {
    setTexts((prev) => prev.filter((t) => t.id !== id));
    if (selectedTextId === id) setSelectedTextId(null);
  };

  const buildBrandText = useCallback(
    (overrides: Partial<TextElement>, role: 'primary' | 'secondary' = 'primary'): TextElement => ({
      id: overrides.id || `t-${Date.now()}`,
      text: overrides.text || 'Text',
      x: overrides.x ?? 50,
      y: overrides.y ?? 50,
      fontSize: overrides.fontSize ?? 32,
      color: overrides.color || (role === 'primary' ? brandPrimaryColor : '#ffffff'),
      fontFamily: overrides.fontFamily || (role === 'primary' ? brandPrimaryFont : brandSecondaryFont),
      bold: overrides.bold ?? false,
      italic: overrides.italic ?? false,
      shadowBlur: overrides.shadowBlur ?? 0,
      shadowColor: overrides.shadowColor || 'rgba(0,0,0,0.7)',
      shadowOffsetX: overrides.shadowOffsetX ?? 0,
      shadowOffsetY: overrides.shadowOffsetY ?? 1,
      strokeColor: overrides.strokeColor || '#000000',
      strokeWidth: overrides.strokeWidth ?? 0,
    }),
    [brandPrimaryColor, brandPrimaryFont, brandSecondaryFont]
  );

  const buildBrandShape = useCallback(
    (overrides: Partial<ShapeElement>, tone: 'primary' | 'secondary' | 'accent' = 'secondary'): ShapeElement => ({
      id: overrides.id || `sh-${Date.now()}`,
      kind: overrides.kind || 'rect',
      x: overrides.x ?? 10,
      y: overrides.y ?? 10,
      w: overrides.w ?? 40,
      h: overrides.h ?? 20,
      fillColor:
        overrides.fillColor ||
        (tone === 'accent' ? brandAccentColor : tone === 'primary' ? brandPrimaryColor : brandSecondaryColor),
      strokeColor: overrides.strokeColor || 'transparent',
      strokeWidth: overrides.strokeWidth ?? 0,
      opacity: overrides.opacity ?? 85,
    }),
    [brandAccentColor, brandPrimaryColor, brandSecondaryColor]
  );

  const applyBrandKitToSelectedText = useCallback(
    (role: 'primary' | 'secondary' = 'primary') => {
      if (!selectedTextId || !activeBrandKit) {
        return;
      }
      updateText(selectedTextId, {
        color: role === 'primary' ? brandPrimaryColor : '#ffffff',
        fontFamily: role === 'primary' ? brandPrimaryFont : brandSecondaryFont,
        bold: role === 'primary',
        italic: role === 'secondary',
      });
      toast.success(
        t('editor_brand_kit_applied', 'Brand kit styling applied to the selected text.')
      );
    },
    [
      activeBrandKit,
      brandPrimaryColor,
      brandPrimaryFont,
      brandSecondaryFont,
      selectedTextId,
      t,
    ]
  );

  /* ─── Shape helpers ─── */
  const addShape = (x: number, y: number, w = 20, h = 20): ShapeElement => {
    const el: ShapeElement = {
      id: `sh-${Date.now()}`, kind: shapeTool,
      x, y, w, h,
      fillColor: shapeFill, strokeColor: shapeStroke,
      strokeWidth: shapeStrokeW, opacity: shapeOpacity,
    };
    setShapes((prev) => [...prev, el]);
    setSelectedShapeId(el.id);
    return el;
  };
  const updateShape = (id: string, u: Partial<ShapeElement>) =>
    setShapes((prev) => prev.map((s) => (s.id === id ? { ...s, ...u } : s)));
  const deleteShape = (id: string) => {
    setShapes((prev) => prev.filter((s) => s.id !== id));
    if (selectedShapeId === id) setSelectedShapeId(null);
  };

  /* ─── Drawing handlers ─── */
  const getRelPos = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgAreaRef.current) return { x: 0, y: 0 };
    const rect = imgAreaRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  const onDrawStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (activeTool === 'eraser') {
      const p = getRelPos(e);
      setEraserPaths((prev) => [...prev, { points: [p], width: eraserWidth }]);
      setIsErasing(true);
    } else if (activeTool === 'draw') {
      const p = getRelPos(e);
      setPaths((prev) => [...prev, { points: [p], color: drawColor, width: drawWidth }]);
      setIsDrawing(true);
    } else if (activeTool === 'shapes') {
      const p = getRelPos(e);
      shapeStartRef.current = p;
      setIsDrawingShape(true);
      // Create a placeholder shape that will be resized on mouse move
      addShape(p.x, p.y, 0.5, 0.5);
    }
  };

  const onDrawMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isErasing && activeTool === 'eraser') {
      const p = getRelPos(e);
      setEraserPaths((prev) => {
        const cp = [...prev];
        const last = cp[cp.length - 1];
        cp[cp.length - 1] = { ...last, points: [...last.points, p] };
        return cp;
      });
    }
    if (isDrawing && activeTool === 'draw') {
      const p = getRelPos(e);
      setPaths((prev) => {
        const cp = [...prev];
        const last = cp[cp.length - 1];
        cp[cp.length - 1] = { ...last, points: [...last.points, p] };
        return cp;
      });
    }
    if (isDrawingShape && activeTool === 'shapes' && shapeStartRef.current) {
      const p = getRelPos(e);
      const start = shapeStartRef.current;
      const x = Math.min(start.x, p.x);
      const y = Math.min(start.y, p.y);
      const w = Math.abs(p.x - start.x);
      const h = Math.abs(p.y - start.y);
      setShapes((prev) => {
        const cp = [...prev];
        const last = cp[cp.length - 1];
        if (last) cp[cp.length - 1] = { ...last, x, y, w: Math.max(1, w), h: Math.max(1, h) };
        return cp;
      });
    }
  };

  const onDrawEnd = () => {
    setIsErasing(false);
    setIsDrawing(false);
    setIsDrawingShape(false);
    shapeStartRef.current = null;
  };

  /* ─── Text drag handlers ─── */
  const onTextMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedTextId(id);
    setDraggingText(id);
  };

  /* ─── Shape drag handlers ─── */
  const onShapeMouseDown = (e: React.MouseEvent, id: string) => {
    if (activeTool !== 'shapes' || isDrawingShape) return;
    e.stopPropagation();
    setSelectedShapeId(id);
    const rect = imgAreaRef.current!.getBoundingClientRect();
    const shape = shapes.find((s) => s.id === id)!;
    shapeDragStartRef.current = {
      mx: ((e.clientX - rect.left) / rect.width) * 100,
      my: ((e.clientY - rect.top) / rect.height) * 100,
      sx: shape.x,
      sy: shape.y,
    };
    setDraggingShapeId(id);
  };

  const onOverlayMouseMove = (e: React.MouseEvent) => {
    if (draggingText && imgAreaRef.current) {
      const rect = imgAreaRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      updateText(draggingText, { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    }
    if (draggingShapeId && shapeDragStartRef.current && imgAreaRef.current) {
      const rect = imgAreaRef.current.getBoundingClientRect();
      const cx = ((e.clientX - rect.left) / rect.width) * 100;
      const cy = ((e.clientY - rect.top) / rect.height) * 100;
      const dx = cx - shapeDragStartRef.current.mx;
      const dy = cy - shapeDragStartRef.current.my;
      updateShape(draggingShapeId, {
        x: Math.max(0, shapeDragStartRef.current.sx + dx),
        y: Math.max(0, shapeDragStartRef.current.sy + dy),
      });
    }
    if (isDrawing) onDrawMove(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
    if (isDrawingShape) onDrawMove(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
  };

  const onOverlayMouseUp = () => {
    setDraggingText(null);
    setDraggingShapeId(null);
    shapeDragStartRef.current = null;
    onDrawEnd();
  };

  /* ─── Crop drag ─── */
  const onCropMouseDown = (e: React.MouseEvent) => {
    if (!isCropping || !imgAreaRef.current) return;
    const rect = imgAreaRef.current.getBoundingClientRect();
    const startX = ((e.clientX - rect.left) / rect.width) * 100;
    const startY = ((e.clientY - rect.top) / rect.height) * 100;
    const onMove = (me: MouseEvent) => {
      const curX = ((me.clientX - rect.left) / rect.width) * 100;
      const curY = ((me.clientY - rect.top) / rect.height) * 100;
      const x = Math.min(startX, curX);
      let y = Math.min(startY, curY);
      const w = Math.abs(curX - startX);
      let h = Math.abs(curY - startY);
      if (cropRatio > 0) { h = w / cropRatio; if (startY > curY) y = startY - h; }
      setCrop({
        x: Math.max(0, Math.min(x, 100 - w)),
        y: Math.max(0, Math.min(y, 100 - h)),
        w: Math.min(w, 100), h: Math.min(h, 100),
      });
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  /* ─── Width/Height with AR lock ─── */
  const setWidth = (w: number) => {
    if (lockAR && originalDims.w > 0) {
      pushEdit({ width: w, height: Math.round(w * (originalDims.h / originalDims.w)) });
    } else { pushEdit({ width: w }); }
  };
  const setHeight = (h: number) => {
    if (lockAR && originalDims.h > 0) {
      pushEdit({ height: h, width: Math.round(h * (originalDims.w / originalDims.h)) });
    } else { pushEdit({ height: h }); }
  };

  const selectTool = (tool: ToolType) => {
    setActiveTool(activeTool === tool ? null : tool);
    if (tool === 'crop') setIsCropping(true); else setIsCropping(false);
  };

  /* ─── Shape SVG rendering helper ─── */
  const renderShapeSvg = (s: ShapeElement) => {
    const key = s.id;
    const isSelected = selectedShapeId === s.id;
    const selProps = {
      strokeDasharray: isSelected ? '4 2' : undefined,
      filter: isSelected ? 'drop-shadow(0 0 3px rgba(255,255,255,0.8))' : undefined,
    };
    const opacity = s.opacity / 100;
    const sw = `${s.strokeWidth * 0.15}`;

    if (s.kind === 'rect') {
      return (
        <rect key={key} x={`${s.x}%`} y={`${s.y}%`} width={`${s.w}%`} height={`${s.h}%`}
          fill={s.fillColor} stroke={s.strokeColor} strokeWidth={sw} opacity={opacity} {...selProps} />
      );
    }
    if (s.kind === 'circle') {
      return (
        <ellipse key={key}
          cx={`${s.x + s.w / 2}%`} cy={`${s.y + s.h / 2}%`}
          rx={`${s.w / 2}%`} ry={`${s.h / 2}%`}
          fill={s.fillColor} stroke={s.strokeColor} strokeWidth={sw} opacity={opacity} {...selProps} />
      );
    }
    if (s.kind === 'line') {
      return (
        <line key={key} x1={`${s.x}%`} y1={`${s.y}%`} x2={`${s.x + s.w}%`} y2={`${s.y + s.h}%`}
          stroke={s.strokeColor} strokeWidth={sw} opacity={opacity} {...selProps} />
      );
    }
    if (s.kind === 'arrow') {
      const angle = Math.atan2(s.h, s.w) * (180 / Math.PI);
      return (
        <g key={key} opacity={opacity} {...selProps}>
          <defs>
            <marker id={`arr-${s.id}`} markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
              <path d="M0,0 L0,6 L6,3 z" fill={s.strokeColor} />
            </marker>
          </defs>
          <line x1={`${s.x}%`} y1={`${s.y}%`} x2={`${s.x + s.w}%`} y2={`${s.y + s.h}%`}
            stroke={s.strokeColor} strokeWidth={sw}
            markerEnd={`url(#arr-${s.id})`}
          />
          <title>{`Arrow ${angle.toFixed(0)}°`}</title>
        </g>
      );
    }
    return null;
  };

  /* ═══ RENDER ═══ */

  // Tool definitions — left sidebar
  const TOOLS = [
    { tool: 'crop' as ToolType,      icon: Crop,       tip: 'Crop' },
    { tool: 'resize' as ToolType,    icon: Maximize2,  tip: 'Resize' },
    { tool: 'filters' as ToolType,   icon: Sliders,    tip: 'Filters' },
    { tool: 'adjust' as ToolType,    icon: SunMedium,  tip: 'Adjust' },
    { tool: 'text' as ToolType,      icon: Type,       tip: 'Text' },
    { tool: 'draw' as ToolType,      icon: Paintbrush, tip: 'Draw' },
    { tool: 'eraser' as ToolType,    icon: Eraser,     tip: 'Eraser' },
    { tool: 'shapes' as ToolType,    icon: Square,     tip: 'Shapes' },
    { tool: 'stickers' as ToolType,  icon: Smile,      tip: 'Stickers' },
    { tool: 'frame' as ToolType,     icon: Frame,      tip: 'Frame / Border' },
    { tool: 'collage' as ToolType,   icon: LayoutGrid, tip: 'Collage' },
    { tool: 'templates' as ToolType, icon: Wand2,      tip: 'Templates' },
    { tool: 'bg-remove' as ToolType, icon: Scissors,   tip: 'Remove BG' },
  ];

  return (
    <div className="flex h-full w-full overflow-hidden bg-[#f6f4ee] text-n-text">

      {/* ─── Left Toolbar ─── */}
      <div className="flex w-[74px] flex-shrink-0 flex-col items-center gap-2 border-r border-n-border bg-[#fbfaf7] px-3 py-4">
        <button
          onClick={handleBack}
          className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-n border border-n-border bg-white text-n-text-md shadow-n-xs transition hover:bg-n-hover hover:text-n-text"
          title={t('editor_go_back', 'Go Back')}
        >
          <ArrowLeft size={20} />
        </button>
        <div className="mb-1 h-px w-10 bg-n-border" />

        {TOOLS.map(({ tool, icon: Icon, tip }) => {
          const isActive = activeTool === tool;
          return (
            <button
              key={tool}
              onClick={() => {
                if (tool === 'bg-remove') { handleBgRemove(); return; }
                selectTool(tool);
              }}
              className={`${shellToolBtn} ${isActive ? shellToolBtnActive : ''}`}
              title={tip}
              disabled={tool === 'bg-remove' && bgRemoving}
            >
              {tool === 'bg-remove' && bgRemoving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Icon size={18} />
              )}
            </button>
          );
        })}
      </div>

      {/* ─── Center canvas workspace ─── */}
      <div className="flex flex-1 flex-col overflow-hidden bg-[#f6f4ee]">

        {/* Top bar */}
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-n-border bg-white px-4 py-3">
          <button onClick={undo} disabled={histIdx === 0}
            className={shellIconBtn} title={t('editor_undo', 'Undo (Ctrl+Z)')}>
            <Undo2 size={15} />
          </button>
          <button onClick={redo} disabled={histIdx === history.length - 1}
            className={shellIconBtn} title={t('editor_redo', 'Redo (Ctrl+Y)')}>
            <Redo2 size={15} />
          </button>
          <div className="mx-1 h-5 w-px bg-n-border" />
          <button onClick={() => pushEdit({ rotate: edit.rotate - 90 })} className={shellIconBtn} title={t('editor_rotate_left', 'Rotate Left')}>
            <RotateCcw size={15} />
          </button>
          <button onClick={() => pushEdit({ rotate: edit.rotate + 90 })} className={shellIconBtn} title={t('editor_rotate_right', 'Rotate Right')}>
            <RotateCw size={15} />
          </button>
          <button onClick={() => pushEdit({ flipH: !edit.flipH })} className={shellIconBtn} title={t('editor_flip_horizontal', 'Flip H')}>
            <FlipHorizontal size={15} />
          </button>
          <button onClick={() => pushEdit({ flipV: !edit.flipV })} className={shellIconBtn} title={t('editor_flip_vertical', 'Flip V')}>
            <FlipVertical size={15} />
          </button>
          <div className="mx-1 h-5 w-px bg-n-border" />
          {/* Zoom controls in topbar */}
          <button onClick={() => pushEdit({ zoom: Math.max(0.25, edit.zoom - 0.1) })} className={shellIconBtn} title="Zoom Out (Ctrl+-)">
            <ZoomOut size={15} />
          </button>
          <span className="w-10 text-center text-[11px] tabular-nums text-n-text-md">
            {Math.round(edit.zoom * 100)}%
          </span>
          <button onClick={() => pushEdit({ zoom: Math.min(3, edit.zoom + 0.1) })} className={shellIconBtn} title="Zoom In (Ctrl+=)">
            <ZoomIn size={15} />
          </button>
          <button onClick={() => pushEdit({ zoom: 1 })} className="px-1 text-[10px] text-n-text-lt transition hover:text-n-text" title="Reset zoom">
            1:1
          </button>

          <div className="flex-1" />
          {image && (
            <span className="mr-3 text-xs text-n-text-lt">
              {originalDims.w} × {originalDims.h}px
            </span>
          )}
          {image && (
            <button
              onClick={() => saveToLibraryMutation.mutate()}
              disabled={saveToLibraryMutation.isPending}
              className="inline-flex h-10 items-center gap-1.5 rounded-n border border-n-green bg-n-green px-4 text-xs font-semibold text-white shadow-[0_12px_24px_-20px_rgba(34,197,94,0.8)] transition hover:brightness-95 disabled:opacity-50"
            >
              {saveToLibraryMutation.isPending ? (
                <><Loader2 size={13} className="animate-spin" />{formatBytes(uploadBytes.loaded)} / {formatBytes(uploadBytes.total)}</>
              ) : (
                <><Save size={13} />{saveActionLabel}</>
              )}
            </button>
          )}
        </div>

        {(editorReturnTo || editorSource) && (
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-n-border bg-[#fbfaf7] px-5 py-4">
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
              {editorContextHeading}
            </span>
            {editorContentTypeLabel && (
              <span className="inline-flex items-center rounded-full border border-n-border bg-white px-2.5 py-1 text-[11px] font-medium text-n-text">
                {editorContentTypeLabel}
              </span>
            )}
            {selectedPlatformLabels.map((label) => (
              <span
                key={label}
                className="inline-flex items-center rounded-full border border-n-border bg-white px-2.5 py-1 text-[11px] font-medium text-n-text-md"
              >
                {label}
              </span>
            ))}
            <p className="basis-full text-xs leading-5 text-n-text-md">
              {editorContextSummary}
            </p>
            {returnsToDraft && (
              <div className="basis-full flex flex-wrap items-center gap-2 pt-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-n-text-lt">
                  {t('editor_apply_mode_label', 'After save')}
                </span>
                <button
                  type="button"
                  onClick={() => setDraftSaveMode('replace_current_media')}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-medium transition ${
                    draftSaveMode === 'replace_current_media'
                      ? 'bg-n-text text-white'
                      : 'border border-n-border bg-white text-n-text-md hover:bg-n-hover'
                  }`}
                >
                  {t('editor_apply_mode_replace', 'Save asset + replace draft')}
                </button>
                <button
                  type="button"
                  onClick={() => setDraftSaveMode('keep_original_use_for_selected_platforms')}
                  disabled={selectedPlatformLabels.length === 0}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-medium transition ${
                    draftSaveMode === 'keep_original_use_for_selected_platforms'
                      ? 'bg-n-text text-white'
                      : 'border border-n-border bg-white text-n-text-md hover:bg-n-hover'
                  }`}
                >
                  {t(
                    'editor_apply_mode_variant',
                    'Save asset + use for selected platforms',
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setDraftSaveMode('save_only')}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-medium transition ${
                    draftSaveMode === 'save_only'
                      ? 'bg-n-text text-white'
                      : 'border border-n-border bg-white text-n-text-md hover:bg-n-hover'
                  }`}
                >
                  {t('editor_apply_mode_library_only', 'Save as asset only')}
                </button>
                <p className="basis-full text-[11px] text-n-text-lt">
                  {t(
                    'editor_apply_mode_hint',
                    'Every save creates a reusable library asset. Choose whether the current draft also changes too.',
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Canvas area */}
        <div
          className="relative flex flex-1 items-center justify-center overflow-auto bg-[#f1ede5] p-6"
          role="presentation"
          onMouseMove={onOverlayMouseMove}
          onMouseUp={onOverlayMouseUp}
        >
          {!image ? (
            <div className="flex h-full max-h-96 w-full max-w-lg flex-col items-center justify-center gap-5">
              <div
                role="button"
                tabIndex={0}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
                className="flex w-full flex-1 cursor-pointer flex-col items-center justify-center gap-3 rounded-[24px] border-2 border-dashed border-n-border bg-white/80 transition hover:bg-white"
              >
                <Upload size={42} className="text-n-text-lt" />
                <p className="text-sm text-n-text-md">{t('editor_drop_image', 'Drop image or click to upload')}</p>
                <p className="text-xs text-n-text-lt">{t('editor_supported_formats', 'Supports JPG, PNG, WebP, GIF')}</p>
              </div>
              <div className="flex w-full items-center gap-3 px-4">
                <div className="h-px flex-1 bg-n-border" />
                <span className="text-xs uppercase tracking-wide text-n-text-lt">{t('editor_or', 'or')}</span>
                <div className="h-px flex-1 bg-n-border" />
              </div>
              <button
                onClick={() => setShowMediaPicker(true)}
                className="flex w-full items-center justify-center gap-2.5 rounded-[18px] border border-n-border bg-white px-4 py-3 text-sm font-medium text-n-text shadow-n-xs transition hover:bg-n-hover"
              >
                <FolderOpen size={18} />
                {t('editor_load_from_library', 'Load from Media Library')}
              </button>
            </div>
          ) : (
            <div
              ref={imgAreaRef}
              className="relative inline-block"
              role="presentation"
              style={{
                cursor: isCropping ? 'crosshair'
                  : activeTool === 'draw' ? 'crosshair'
                  : activeTool === 'shapes' ? 'crosshair'
                  : draggingText || draggingShapeId ? 'move'
                  : 'default',
              }}
              onMouseDown={(e) => {
                if (isCropping) onCropMouseDown(e);
                else if (activeTool === 'draw') onDrawStart(e);
                else if (activeTool === 'shapes') onDrawStart(e);
              }}
            >
              {/* Checkerboard transparency bg */}
                  <div style={{
                    backgroundImage: 'linear-gradient(45deg, #e7e2d7 25%, transparent 25%, transparent 75%, #e7e2d7 75%), linear-gradient(45deg, #e7e2d7 25%, transparent 25%, transparent 75%, #e7e2d7 75%)',
                    backgroundSize: '16px 16px', backgroundPosition: '0 0, 8px 8px', backgroundColor: '#f6f2e8',
                  }}>
                <img
                  src={image}
                  alt="Edit"
                  className="block max-w-full"
                  style={{ maxHeight: '65vh', filter: cssFilter, transform: cssTransform, transformOrigin: 'center' }}
                  draggable={false}
                />
              </div>

              {/* Vignette preview overlay */}
              {edit.vignetteIntensity > 0 && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,${(edit.vignetteIntensity / 100) * 0.85}) 100%)`,
                  }}
                />
              )}

              {/* Crop overlay */}
              {isCropping && (
                <>
                  <svg className="absolute inset-0 w-full h-full pointer-events-none">
                    <defs>
                      <mask id="crop-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <rect x={`${crop.x}%`} y={`${crop.y}%`} width={`${crop.w}%`} height={`${crop.h}%`} fill="black" />
                      </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#crop-mask)" />
                  </svg>
                  <div className="absolute border-2 border-white pointer-events-none" style={{
                    left: `${crop.x}%`, top: `${crop.y}%`, width: `${crop.w}%`, height: `${crop.h}%`,
                  }}>
                    {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((pos) => (
                      <div key={pos} className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm"
                        style={{ ...(pos.includes('top') ? { top: -5 } : { bottom: -5 }), ...(pos.includes('left') ? { left: -5 } : { right: -5 }) }}
                      />
                    ))}
                    <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
                      {Array.from({ length: 9 }).map((_, i) => <div key={i} className="border border-white/20" />)}
                    </div>
                  </div>
                </>
              )}

              {/* Shape + Draw SVG overlay */}
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ pointerEvents: activeTool === 'shapes' ? 'none' : 'none' }}
              >
                {/* Shapes */}
                {shapes.map((s) => (
                  <g
                    key={s.id}
                    style={{ pointerEvents: activeTool === 'shapes' ? 'all' : 'none', cursor: 'move' }}
                    onMouseDown={(e) => onShapeMouseDown(e as unknown as React.MouseEvent, s.id)}
                  >
                    {renderShapeSvg(s)}
                  </g>
                ))}
                {/* Draw paths */}
                {paths.map((p, i) => (
                  <polyline
                    key={i}
                    points={p.points.map((pt) => `${pt.x},${pt.y}`).join(' ')}
                    stroke={p.color}
                    strokeWidth={p.width * 0.15}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </svg>

              {/* Text overlays */}
              {texts.map((t) => (
                <div
                  key={t.id}
                  className="absolute select-none"
                  style={{
                    left: `${t.x}%`, top: `${t.y}%`, transform: 'translate(-50%,-50%)',
                    fontSize: `${t.fontSize}px`, color: t.color,
                    fontWeight: t.bold ? 'bold' : 'normal',
                    fontStyle: t.italic ? 'italic' : 'normal',
                    fontFamily: `"${t.fontFamily || 'Arial'}", Arial, sans-serif`,
                    cursor: 'move',
                    border: selectedTextId === t.id ? '1px dashed rgba(255,255,255,0.7)' : 'none',
                    padding: '2px 6px',
                    textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                    pointerEvents: activeTool === 'draw' || isCropping || activeTool === 'shapes' ? 'none' : 'auto',
                  }}
                  role="presentation"
                  onMouseDown={(e) => onTextMouseDown(e, t.id)}
                >
                  {t.text}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom bar */}
        {image && (
          <div className="flex flex-shrink-0 items-center gap-2 border-t border-n-border bg-white px-4 py-2.5">
            <button onClick={() => pushEdit({ zoom: Math.max(0.25, edit.zoom - 0.1) })} className="rounded-n-sm p-1.5 hover:bg-n-hover">
              <ZoomOut size={14} className="text-n-text-md" />
            </button>
            <input type="range" min={25} max={300} value={Math.round(edit.zoom * 100)}
              onChange={(e) => pushEdit({ zoom: parseInt(e.target.value) / 100 })}
              className="w-24" style={{ accentColor: pc }}
            />
            <span className="w-10 text-xs tabular-nums text-n-text-lt">{Math.round(edit.zoom * 100)}%</span>
            <button onClick={() => pushEdit({ zoom: Math.min(3, edit.zoom + 0.1) })} className="rounded-n-sm p-1.5 hover:bg-n-hover">
              <ZoomIn size={14} className="text-n-text-md" />
            </button>
            <div className="flex-1" />
            <span className="mr-2 text-[10px] text-n-text-lt">Ctrl+Z/Y · Ctrl+S · Ctrl+±</span>
            <button onClick={() => setShowMediaPicker(true)}
              className="flex items-center gap-1.5 rounded-n-sm px-3 py-1.5 text-xs text-n-text-md transition hover:bg-n-hover hover:text-n-text">
              <FolderOpen size={13} />
              {t('editor_load_from_library_short', 'Library')}
            </button>
            <button onClick={() => { setImage(null); setTexts([]); setPaths([]); setShapes([]); }}
              className="rounded-n-sm px-3 py-1.5 text-xs text-n-text-md transition hover:bg-n-hover hover:text-n-text">
              {t('editor_new_image', 'New Image')}
            </button>
          </div>
        )}
      </div>

      {/* ─── Right Panel ─── */}
      {image && (
        <div className="w-80 flex-shrink-0 overflow-y-auto border-l border-n-border bg-[#fbfaf7]">
          <div className="space-y-5 p-4 text-n-text">

            {/* Active tool label */}
            {activeTool && (
              <div className="flex items-center gap-2 border-b border-n-border pb-3">
                <h3 className="text-sm font-semibold capitalize text-n-text">
                  {activeTool === 'bg-remove' ? 'Remove Background' : activeTool}
                </h3>
              </div>
            )}

            <div className="rounded-n border border-n-border bg-white p-3 shadow-n-xs">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-n-text-lt">
                    {t('editor_brand_kit_heading', 'Brand kit')}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-n-text-md">
                    {t(
                      'editor_brand_kit_hint',
                      'New text and templates inherit the active workspace brand style.'
                    )}
                  </p>
                </div>
                <Palette className="mt-0.5 h-4 w-4 text-n-green" />
              </div>
              {brandKits.length > 0 ? (
                <>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {brandKits.map((kit) => {
                      const isSelected = selectedBrandKitId === kit.id;
                      return (
                        <button
                          key={kit.id}
                          type="button"
                          onClick={() => setSelectedBrandKitId(kit.id)}
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-medium transition ${
                            isSelected
                              ? 'border-n-green bg-[rgba(34,197,94,0.08)] text-n-green'
                              : 'border-n-border bg-white text-n-text-md hover:bg-n-hover hover:text-n-text'
                          }`}
                        >
                          <span
                            className="h-2.5 w-2.5 rounded-full border border-white/70"
                            style={{ backgroundColor: kit.primary_color || brandPrimaryColor }}
                          />
                          {kit.name}
                        </button>
                      );
                    })}
                  </div>
                  {activeBrandKit && (
                    <div className="mt-3 rounded-n-sm border border-n-border bg-[#f7f6f3] p-3">
                      <div className="flex items-center gap-2">
                        <span className="h-6 w-6 rounded-full border border-white/70" style={{ backgroundColor: activeBrandKit.primary_color || brandPrimaryColor }} />
                        <span className="h-6 w-6 rounded-full border border-white/70" style={{ backgroundColor: activeBrandKit.secondary_color || brandSecondaryColor }} />
                        {(activeBrandKit.accent_colors || []).slice(0, 2).map((color, index) => (
                          <span key={`${color}-${index}`} className="h-6 w-6 rounded-full border border-white/70" style={{ backgroundColor: color }} />
                        ))}
                      </div>
                      <p className="mt-2 text-[11px] text-n-text-md">
                        {activeBrandKit.primary_font || brandPrimaryFont} · {activeBrandKit.secondary_font || brandSecondaryFont}
                      </p>
                      {selectedTextId && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => applyBrandKitToSelectedText('primary')}
                            className="rounded-full border border-n-border bg-white px-3 py-1.5 text-[11px] font-medium text-n-text transition hover:bg-n-hover"
                          >
                            {t('editor_brand_kit_apply_heading', 'Apply heading style')}
                          </button>
                          <button
                            type="button"
                            onClick={() => applyBrandKitToSelectedText('secondary')}
                            className="rounded-full border border-n-border bg-white px-3 py-1.5 text-[11px] font-medium text-n-text transition hover:bg-n-hover"
                          >
                            {t('editor_brand_kit_apply_supporting', 'Apply supporting style')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="mt-3 text-xs text-n-text-lt">
                  {t(
                    'editor_brand_kit_empty',
                    'No workspace brand kits yet. The editor will use its default fonts and colors.'
                  )}
                </p>
              )}
            </div>

            {/* CROP */}
            {activeTool === 'crop' && (
              <div className="space-y-3">
                <p className="text-xs text-n-text-md">{t('editor_crop_desc', 'Click & drag on the image to select a crop region.')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {getCropPresets(t).map((p) => (
                    <button key={p.label} onClick={() => { setCropRatio(p.ratio); setIsCropping(true); }}
                      className={`px-2.5 py-1 text-xs rounded border transition
                        ${cropRatio === p.ratio ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-600 text-gray-300 hover:border-gray-400'}`}
                    >{p.label}</button>
                  ))}
                </div>
                <p className="mt-2 text-[10px] font-medium uppercase tracking-wider text-n-text-lt">Platform Sizes</p>
                <div className="space-y-1">
                  {getPlatformSizes(t).map((p) => (
                    <button key={p.label} onClick={() => { setCropRatio(p.w / p.h); setIsCropping(true); }}
                      className="w-full rounded-n-sm border border-n-border bg-white px-2.5 py-1.5 text-left text-xs text-n-text-md transition hover:bg-n-hover hover:text-n-text">
                      {p.label} ({p.w}×{p.h})
                    </button>
                  ))}
                </div>
                <button onClick={applyCrop} className="w-full py-2 text-sm font-medium text-white rounded-lg bg-blue-600 hover:bg-blue-500 transition">
                  {t('editor_apply_crop', 'Apply Crop')}
                </button>
              </div>
            )}

            {/* RESIZE */}
            {activeTool === 'resize' && (
              <div className="space-y-3">
                <div>
                  <label htmlFor={widthId} className="text-xs text-n-text-md">Width (px)</label>
                  <input id={widthId} type="number" value={edit.width} onChange={(e) => setWidth(parseInt(e.target.value) || 0)}
                    className={panelFieldClass} />
                </div>
                <div>
                  <label htmlFor={heightId} className="text-xs text-n-text-md">Height (px)</label>
                  <input id={heightId} type="number" value={edit.height} onChange={(e) => setHeight(parseInt(e.target.value) || 0)}
                    className={panelFieldClass} />
                </div>
                <button onClick={() => setLockAR(!lockAR)} className="flex items-center gap-2 text-xs text-n-text-md hover:text-n-text">
                  {lockAR ? <Lock size={14} /> : <Unlock size={14} />}
                  {lockAR ? 'Aspect ratio locked' : 'Aspect ratio unlocked'}
                </button>
                <p className="text-[10px] font-medium uppercase tracking-wider text-n-text-lt">Presets</p>
                <div className="space-y-1">
                  {getPlatformSizes(t).map((p) => (
                    <button key={p.label} onClick={() => pushEdit({ width: p.w, height: p.h })}
                      className="w-full rounded-n-sm border border-n-border bg-white px-2.5 py-1.5 text-left text-xs text-n-text-md transition hover:bg-n-hover hover:text-n-text">
                      {p.label} ({p.w}×{p.h})
                    </button>
                  ))}
                </div>
                <button onClick={applyResize} className="w-full py-2 text-sm font-medium text-white rounded-lg bg-blue-600 hover:bg-blue-500 transition">
                  Apply Resize
                </button>
              </div>
            )}

            {/* FILTERS */}
            {activeTool === 'filters' && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-1.5">
                  {getFilterPresets(t).map((f) => (
                    <button key={f.name} onClick={() => { setActiveFilter(f.name); pushEdit(f.overrides); }}
                      className={`p-2 text-xs rounded-lg border transition text-center
                        ${activeFilter === f.name ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-600 text-gray-300 hover:border-gray-400'}`}
                    >{f.label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* ADJUST (+ Vignette / Sharpen / Noise) */}
            {activeTool === 'adjust' && (
              <div className="space-y-3">
                {[
                  { label: 'Brightness', key: 'brightness' as const, min: 0, max: 200 },
                  { label: 'Contrast', key: 'contrast' as const, min: 0, max: 200 },
                  { label: 'Saturation', key: 'saturation' as const, min: 0, max: 200 },
                  { label: 'Blur', key: 'blur' as const, min: 0, max: 20 },
                  { label: 'Hue Rotate', key: 'hueRotate' as const, min: 0, max: 360 },
                  { label: 'Opacity', key: 'opacity' as const, min: 0, max: 100 },
                ].map((s) => (
                  <div key={s.key}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-400">{s.label}</span>
                      <span className="text-gray-500 tabular-nums">{Math.round(edit[s.key])}</span>
                    </div>
                    <input type="range" min={s.min} max={s.max} value={edit[s.key]}
                      onChange={(e) => pushEdit({ [s.key]: parseInt(e.target.value) })}
                      className="w-full" style={{ accentColor: '#3b82f6' }} />
                  </div>
                ))}

                {/* Divider */}
                <div className="h-px bg-gray-700 my-1" />
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">New Effects</p>

                {/* Vignette */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <label htmlFor={vignetteId} className="text-gray-400">Vignette</label>
                    <span className="text-gray-500 tabular-nums">{edit.vignetteIntensity}</span>
                  </div>
                  <input id={vignetteId} type="range" min={0} max={100} value={edit.vignetteIntensity}
                    onChange={(e) => pushEdit({ vignetteIntensity: parseInt(e.target.value) })}
                    className="w-full" style={{ accentColor: '#8b5cf6' }} />
                </div>

                {/* Sharpen */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <label htmlFor={sharpenId} className="text-gray-400">Sharpen</label>
                    <span className="text-gray-500 tabular-nums">{edit.sharpen}</span>
                  </div>
                  <input id={sharpenId} type="range" min={0} max={100} value={edit.sharpen}
                    onChange={(e) => pushEdit({ sharpen: parseInt(e.target.value) })}
                    className="w-full" style={{ accentColor: '#10b981' }} />
                </div>

                {/* Noise / Grain */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <label htmlFor={noiseId} className="text-gray-400">Grain / Noise</label>
                    <span className="text-gray-500 tabular-nums">{edit.noise}</span>
                  </div>
                  <input id={noiseId} type="range" min={0} max={100} value={edit.noise}
                    onChange={(e) => pushEdit({ noise: parseInt(e.target.value) })}
                    className="w-full" style={{ accentColor: '#f59e0b' }} />
                </div>

                <label htmlFor={invertId} className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                  <input id={invertId} type="checkbox" checked={edit.invert} onChange={() => pushEdit({ invert: !edit.invert })} className="rounded" />
                  Invert Colors
                </label>

                <button onClick={() => pushEdit({ brightness: 100, contrast: 100, saturation: 100, blur: 0, hueRotate: 0, opacity: 100, invert: false, vignetteIntensity: 0, sharpen: 0, noise: 0 })}
                  className="w-full py-1.5 text-xs text-gray-400 border border-gray-700 rounded hover:bg-gray-700 transition">
                  Reset All Adjustments
                </button>
              </div>
            )}

            {/* TEXT (+ Font Family selector) */}
            {activeTool === 'text' && (
              <div className="space-y-3">
                <button onClick={addText} className="w-full rounded-[18px] bg-n-text py-2.5 text-sm font-medium text-white transition hover:brightness-95">
                  + {t('editor_add_text', 'Add Text')}
                </button>
                {selectedTextId && (() => {
                  const sel = texts.find((t) => t.id === selectedTextId);
                  if (!sel) return null;
                  return (
                    <div className="space-y-2 rounded-n border border-n-border bg-white p-3 shadow-n-xs">
                      <input type="text" value={sel.text} onChange={(e) => updateText(sel.id, { text: e.target.value })}
                        className="w-full rounded-n-sm border border-n-border bg-white px-2.5 py-1.5 text-sm text-n-text focus:outline-none focus:ring-2 focus:ring-n-green/20 focus:border-n-green" />

                      {/* Font Family */}
                      <div>
                        <label htmlFor={textFontId} className="mb-1 block text-[10px] uppercase tracking-wider text-n-text-lt">Font</label>
                        <select
                          id={textFontId}
                          value={sel.fontFamily}
                          onChange={(e) => updateText(sel.id, { fontFamily: e.target.value })}
                          className="w-full rounded-n-sm border border-n-border bg-white px-2 py-1.5 text-xs text-n-text focus:outline-none focus:ring-2 focus:ring-n-green/20 focus:border-n-green"
                          style={{ fontFamily: sel.fontFamily }}
                        >
                          {FONT_FAMILIES.map((f) => (
                            <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex gap-2 items-center">
                        <label htmlFor={textSizeId} className="w-10 text-xs text-n-text-md">Size</label>
                        <input id={textSizeId} type="range" min={12} max={120} value={sel.fontSize}
                          onChange={(e) => updateText(sel.id, { fontSize: parseInt(e.target.value) })}
                          className="flex-1" style={{ accentColor: '#3b82f6' }} />
                        <span className="w-6 text-xs tabular-nums text-n-text-lt">{sel.fontSize}</span>
                      </div>
                      <div className="flex gap-2 items-center">
                        <label htmlFor={textColorId} className="w-10 text-xs text-n-text-md">Color</label>
                        <input id={textColorId} type="color" value={sel.color} onChange={(e) => updateText(sel.id, { color: e.target.value })}
                          className="h-7 w-7 cursor-pointer rounded border border-n-border bg-transparent" />
                        <span className="text-xs text-n-text-lt">{sel.color}</span>
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={() => updateText(sel.id, { bold: !sel.bold })}
                          className={`rounded px-2.5 py-1 text-xs font-bold transition ${sel.bold ? 'bg-n-text text-white' : 'border border-n-border bg-[#f7f6f3] text-n-text-md hover:bg-n-hover'}`}>B</button>
                        <button onClick={() => updateText(sel.id, { italic: !sel.italic })}
                          className={`rounded px-2.5 py-1 text-xs italic transition ${sel.italic ? 'bg-n-text text-white' : 'border border-n-border bg-[#f7f6f3] text-n-text-md hover:bg-n-hover'}`}>I</button>
                        <div className="flex-1" />
                        <button onClick={() => deleteText(sel.id)} className="px-2 py-1 text-xs text-red-400 hover:bg-red-900/30 rounded">
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <p className="rounded-n-sm border border-n-border bg-[#f7f6f3] px-2.5 py-2 text-[11px] leading-5 text-n-text-md">
                        {t(
                          'editor_brand_kit_active_hint',
                          'The active brand kit already powers new text and template layers. Use the quick actions above to re-style the current text layer.'
                        )}
                      </p>
                      {/* Text Stroke */}
                      <div className="my-1 h-px bg-n-border" />
                      <p className="text-[10px] uppercase tracking-wider text-n-text-lt">Stroke</p>
                      <div className="flex gap-2 items-center">
                        <span className="w-10 text-xs text-n-text-md">Color</span>
                        <input type="color" value={sel.strokeColor || '#000000'} onChange={(e) => updateText(sel.id, { strokeColor: e.target.value })}
                          className="h-7 w-7 cursor-pointer rounded border border-n-border bg-transparent" />
                      </div>
                      <div className="flex gap-2 items-center">
                        <span className="w-10 text-xs text-n-text-md">Width</span>
                        <input type="range" min={0} max={15} value={sel.strokeWidth ?? 0}
                          onChange={(e) => updateText(sel.id, { strokeWidth: parseInt(e.target.value) })}
                          className="flex-1" style={{ accentColor: '#3b82f6' }} />
                        <span className="w-4 text-xs tabular-nums text-n-text-lt">{sel.strokeWidth ?? 0}</span>
                      </div>
                      {/* Text Shadow */}
                      <p className="mt-1 text-[10px] uppercase tracking-wider text-n-text-lt">Shadow</p>
                      <div className="flex gap-2 items-center">
                        <span className="w-10 text-xs text-n-text-md">Color</span>
                        <input type="color" value={sel.shadowColor || 'rgba(0,0,0,0.8)'} onChange={(e) => updateText(sel.id, { shadowColor: e.target.value })}
                          className="h-7 w-7 cursor-pointer rounded border border-n-border bg-transparent" />
                      </div>
                      <div className="flex gap-2 items-center">
                        <span className="w-10 text-xs text-n-text-md">Blur</span>
                        <input type="range" min={0} max={30} value={sel.shadowBlur ?? 0}
                          onChange={(e) => updateText(sel.id, { shadowBlur: parseInt(e.target.value) })}
                          className="flex-1" style={{ accentColor: '#8b5cf6' }} />
                        <span className="w-4 text-xs tabular-nums text-n-text-lt">{sel.shadowBlur ?? 0}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-[10px] text-n-text-lt">Offset X</span>
                          <input type="range" min={-20} max={20} value={sel.shadowOffsetX ?? 0}
                            onChange={(e) => updateText(sel.id, { shadowOffsetX: parseInt(e.target.value) })}
                            className="w-full" style={{ accentColor: '#8b5cf6' }} />
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-500">Offset Y</span>
                          <input type="range" min={-20} max={20} value={sel.shadowOffsetY ?? 0}
                            onChange={(e) => updateText(sel.id, { shadowOffsetY: parseInt(e.target.value) })}
                            className="w-full" style={{ accentColor: '#8b5cf6' }} />
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500">Drag on image to reposition. Del to remove.</p>
                    </div>
                  );
                })()}
                {texts.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Layers</span>
                    {texts.map((t) => (
                      <button key={t.id} onClick={() => setSelectedTextId(t.id)}
                        className={`w-full text-left px-2.5 py-1.5 text-xs rounded transition truncate
                          ${selectedTextId === t.id ? 'bg-blue-900/50 text-blue-300' : 'text-gray-400 hover:bg-gray-700'}`}
                        style={{ fontFamily: t.fontFamily }}>
                        {t.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* SHAPES (Tier 1 #1) */}
            {activeTool === 'shapes' && (
              <div className="space-y-3">
                <p className="text-xs text-gray-400">Click & drag on the canvas to draw a shape.</p>

                {/* Shape type selector */}
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Shape</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {([
                      { kind: 'rect' as ShapeKind, Icon: Square, label: 'Rect' },
                      { kind: 'circle' as ShapeKind, Icon: Circle, label: 'Circle' },
                      { kind: 'arrow' as ShapeKind, Icon: ArrowRight, label: 'Arrow' },
                      { kind: 'line' as ShapeKind, Icon: Minus, label: 'Line' },
                    ]).map(({ kind, Icon, label }) => (
                      <button key={kind} onClick={() => setShapeTool(kind)}
                        title={label}
                        className={`flex flex-col items-center gap-0.5 py-2 rounded border text-xs transition
                          ${shapeTool === kind ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-600 text-gray-400 hover:border-gray-400'}`}>
                        <Icon size={16} />
                        <span className="text-[9px]">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fill + Stroke */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Fill</p>
                    <input type="color" value={shapeFill} onChange={(e) => {
                      setShapeFill(e.target.value);
                      if (selectedShapeId) updateShape(selectedShapeId, { fillColor: e.target.value });
                    }} className="w-full h-8 rounded cursor-pointer border border-gray-600 bg-transparent" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Stroke</p>
                    <input type="color" value={shapeStroke} onChange={(e) => {
                      setShapeStroke(e.target.value);
                      if (selectedShapeId) updateShape(selectedShapeId, { strokeColor: e.target.value });
                    }} className="w-full h-8 rounded cursor-pointer border border-gray-600 bg-transparent" />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Stroke Width</span>
                    <span className="text-gray-500 tabular-nums">{shapeStrokeW}px</span>
                  </div>
                  <input type="range" min={1} max={20} value={shapeStrokeW} onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setShapeStrokeW(v);
                    if (selectedShapeId) updateShape(selectedShapeId, { strokeWidth: v });
                  }} className="w-full" style={{ accentColor: '#3b82f6' }} />
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Opacity</span>
                    <span className="text-gray-500 tabular-nums">{shapeOpacity}%</span>
                  </div>
                  <input type="range" min={0} max={100} value={shapeOpacity} onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setShapeOpacity(v);
                    if (selectedShapeId) updateShape(selectedShapeId, { opacity: v });
                  }} className="w-full" style={{ accentColor: '#3b82f6' }} />
                </div>

                {/* Shape layers list */}
                {shapes.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">Layers ({shapes.length})</p>
                    {shapes.map((s) => (
                      <div
                        key={s.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedShapeId === s.id}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs cursor-pointer transition
                        ${selectedShapeId === s.id ? 'bg-blue-900/50 text-blue-300' : 'text-gray-400 hover:bg-gray-700'}`}
                        onClick={() => setSelectedShapeId(s.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedShapeId(s.id);
                          }
                        }}
                      >
                        <span className="capitalize flex-1">{s.kind}</span>
                        <button onClick={(e) => { e.stopPropagation(); deleteShape(s.id); }}
                          className="p-0.5 text-red-400 hover:text-red-300"><Trash2 size={11} /></button>
                      </div>
                    ))}
                    <button onClick={() => setShapes([])} className="w-full py-1.5 text-xs text-red-400 border border-red-900/50 rounded hover:bg-red-900/20 transition">
                      Clear All Shapes
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* DRAW */}
            {activeTool === 'draw' && (
              <div className="space-y-3">
                <p className="text-xs text-gray-400">Click & drag to draw freehand.</p>
                <div className="flex gap-2 items-center">
                  <label htmlFor={drawColorId} className="text-xs text-gray-400 w-10">Color</label>
                  <input id={drawColorId} type="color" value={drawColor} onChange={(e) => setDrawColor(e.target.value)}
                    className="w-7 h-7 rounded cursor-pointer border border-gray-600 bg-transparent" />
                </div>
                <div className="flex gap-2 items-center">
                  <label htmlFor={drawWidthId} className="text-xs text-gray-400 w-10">Size</label>
                  <input id={drawWidthId} type="range" min={1} max={20} value={drawWidth}
                    onChange={(e) => setDrawWidth(parseInt(e.target.value))}
                    className="flex-1" style={{ accentColor: '#3b82f6' }} />
                  <span className="text-xs text-gray-500 w-4 tabular-nums">{drawWidth}</span>
                </div>
                <p className="text-xs text-gray-500">{paths.length} path{paths.length !== 1 ? 's' : ''}</p>
                {paths.length > 0 && (
                  <button onClick={() => setPaths([])} className="w-full py-1.5 text-xs text-red-400 border border-red-900/50 rounded hover:bg-red-900/20 transition">
                    Clear All Paths
                  </button>
                )}
              </div>
            )}

            {/* STICKERS */}
            {activeTool === 'stickers' && (
              <div className="space-y-3">
                <div className="grid grid-cols-6 gap-1">
                  {EMOJIS.map((emoji, i) => (
                    <button key={i} onClick={() => {
                      const textEl: TextElement = {
                        id: `stk-${Date.now()}-${i}`, text: emoji,
                        x: 30 + Math.random() * 40, y: 30 + Math.random() * 40,
                        fontSize: 48, color: '#fff', fontFamily: 'Arial', bold: false, italic: false,
                        shadowBlur: 0, shadowColor: 'rgba(0,0,0,0.8)', shadowOffsetX: 0, shadowOffsetY: 0,
                        strokeColor: '#000000', strokeWidth: 0,
                      };
                      setTexts((prev) => [...prev, textEl]);
                      setSelectedTextId(textEl.id);
                      setActiveTool('text');
                    }}
                      className="text-xl p-1.5 rounded hover:bg-gray-700 transition"
                    >{emoji}</button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">Click to add, then switch to Text to edit.</p>
              </div>
            )}

            {/* ERASER */}
            {activeTool === 'eraser' && (
              <div className="space-y-3">
                <p className="text-xs text-gray-400">Drag over the image to erase pixels (creates transparency on export).</p>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-gray-400 w-10">Size</span>
                  <input type="range" min={4} max={80} value={eraserWidth}
                    onChange={(e) => setEraserWidth(parseInt(e.target.value))}
                    className="flex-1" style={{ accentColor: '#ef4444' }} />
                  <span className="text-xs text-gray-500 w-6 tabular-nums">{eraserWidth}</span>
                </div>
                <p className="text-xs text-gray-500">{eraserPaths.reduce((a, p) => a + p.points.length, 0)} eraser points</p>
                {eraserPaths.length > 0 && (
                  <button onClick={() => setEraserPaths([])}
                    className="w-full py-1.5 text-xs text-red-400 border border-red-900/50 rounded hover:bg-red-900/20 transition">
                    Clear All Erasing
                  </button>
                )}
                <div className="text-[10px] text-gray-600 bg-gray-800 rounded p-2">
                  💡 Export as PNG to preserve transparency. JPG will fill erased areas with white.
                </div>
              </div>
            )}

            {/* FRAME / BORDER */}
            {activeTool === 'frame' && (
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={frame.enabled} onChange={(e) => setFrame((f) => ({ ...f, enabled: e.target.checked }))}
                    className="rounded" />
                  Enable Frame
                </label>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Style</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(['solid', 'gradient-lr', 'gradient-tb', 'double'] as FrameStyle[]).map((s) => (
                      <button key={s} onClick={() => setFrame((f) => ({ ...f, style: s, enabled: true }))}
                        className={`py-1.5 text-xs rounded border transition capitalize ${frame.style === s ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-600 text-gray-400 hover:border-gray-400'}`}>
                        {s.replace('-', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] text-gray-500 mb-1.5">Color 1</p>
                    <input type="color" value={frame.color1} onChange={(e) => setFrame((f) => ({ ...f, color1: e.target.value, enabled: true }))}
                      className="w-full h-8 rounded cursor-pointer border border-gray-600 bg-transparent" />
                  </div>
                  {frame.style !== 'solid' && (
                    <div>
                      <p className="text-[10px] text-gray-500 mb-1.5">Color 2</p>
                      <input type="color" value={frame.color2} onChange={(e) => setFrame((f) => ({ ...f, color2: e.target.value }))}
                        className="w-full h-8 rounded cursor-pointer border border-gray-600 bg-transparent" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Width</span>
                    <span className="text-gray-500 tabular-nums">{frame.width}px</span>
                  </div>
                  <input type="range" min={4} max={120} value={frame.width} onChange={(e) => setFrame((f) => ({ ...f, width: parseInt(e.target.value), enabled: true }))}
                    className="w-full" style={{ accentColor: '#3b82f6' }} />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Opacity</span>
                    <span className="text-gray-500 tabular-nums">{frame.opacity}%</span>
                  </div>
                  <input type="range" min={10} max={100} value={frame.opacity} onChange={(e) => setFrame((f) => ({ ...f, opacity: parseInt(e.target.value) }))}
                    className="w-full" style={{ accentColor: '#3b82f6' }} />
                </div>
                {/* Live preview strip */}
                {frame.enabled && (
                  <div className="rounded overflow-hidden h-10 relative border border-gray-700"
                    style={{
                      background: frame.style === 'solid' ? frame.color1
                        : frame.style === 'gradient-lr' ? `linear-gradient(to right, ${frame.color1}, ${frame.color2})`
                        : frame.style === 'gradient-tb' ? `linear-gradient(to bottom, ${frame.color1}, ${frame.color2})`
                        : `linear-gradient(45deg, ${frame.color1} 50%, ${frame.color2} 50%)`,
                    }}>
                    <div className="absolute inset-[6px] bg-gray-700 rounded-sm" />
                  </div>
                )}
              </div>
            )}

            {/* COLLAGE */}
            {activeTool === 'collage' && (
              <div className="space-y-3">
                <p className="text-xs text-gray-400">Combine 2–4 images into one canvas. Existing edits will be cleared.</p>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Layout</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(['1+1', '2+1', 'grid-4'] as CollageLayout[]).map((l) => (
                      <button key={l} onClick={() => setCollageLayout(l)}
                        className={`py-1.5 text-xs rounded border transition ${collageLayout === l ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-600 text-gray-400 hover:border-gray-400'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Images</p>
                  {collageSlots.slice(0, collageLayout === '1+1' ? 2 : collageLayout === '2+1' ? 3 : 4).map((slot, i) => (
                    <div key={slot.id} className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded border border-gray-600 overflow-hidden flex-shrink-0 bg-gray-800 flex items-center justify-center">
                        {slot.src
                          ? <img src={slot.src} alt={slot.label} className="w-full h-full object-cover" />
                          : <ImageIcon size={16} className="text-gray-600" />}
                      </div>
                      <span className="text-xs text-gray-400 flex-1">{slot.label}</span>
                      <button
                        onClick={() => { setCollagePickingSlot(i); setShowMediaPicker(true); }}
                        className="px-2 py-1 text-xs rounded border border-gray-600 hover:border-gray-400 text-gray-400 hover:text-gray-200 transition">
                        {slot.src ? 'Change' : <Plus size={12} />}
                      </button>
                    </div>
                  ))}
                </div>
                <button onClick={flattenCollage}
                  className="w-full py-2 text-sm font-medium text-white rounded-lg bg-blue-600 hover:bg-blue-500 transition">
                  Create Collage
                </button>
              </div>
            )}

            {/* TEMPLATES */}
            {activeTool === 'templates' && (
              <div className="space-y-3">
                <p className="text-xs text-n-text-md">
                  {t(
                    'editor_templates_hint',
                    'Apply a pre-built layout. Layers are added on top of your current image and inherit the active brand kit.'
                  )}
                </p>
                {[
                  {
                    name: 'Quote Card',
                    desc: 'Semi-transparent box + centered quote text',
                    apply: () => {
                      const box = buildBrandShape({ kind: 'rect', x: 8, y: 30, w: 84, h: 40, opacity: 55 }, 'secondary');
                      const txt = buildBrandText(
                        { text: '"Your quote here"', x: 50, y: 50, fontSize: 28, color: '#ffffff', italic: true, shadowBlur: 4, shadowColor: 'rgba(0,0,0,0.9)', shadowOffsetX: 0, shadowOffsetY: 2 },
                        'secondary'
                      );
                      setShapes((p) => [...p, box]); setTexts((p) => [...p, txt]); setSelectedTextId(txt.id);
                    },
                  },
                  {
                    name: 'Promo Banner',
                    desc: 'Bottom gradient bar with bold promo text + CTA',
                    apply: () => {
                      const bar = buildBrandShape({ kind: 'rect', x: 0, y: 70, w: 100, h: 30, opacity: 85 }, 'accent');
                      const main = buildBrandText(
                        { text: '50% OFF TODAY', x: 50, y: 80, fontSize: 36, color: '#ffffff', shadowBlur: 2, shadowColor: 'rgba(0,0,0,0.7)', shadowOffsetX: 0, shadowOffsetY: 1 },
                        'primary'
                      );
                      const sub = buildBrandText(
                        { id: `t-${Date.now() + 1}`, text: 'Shop Now →', x: 50, y: 91, fontSize: 16, color: brandPrimaryColor },
                        'secondary'
                      );
                      setShapes((p) => [...p, bar]); setTexts((p) => [...p, main, sub]);
                    },
                  },
                  {
                    name: 'Story Headline',
                    desc: 'Top + bottom bars with headline and handle',
                    apply: () => {
                      const top = buildBrandShape({ kind: 'rect', x: 0, y: 0, w: 100, h: 18, opacity: 65 }, 'secondary');
                      const bot = buildBrandShape({ id: `sh-${Date.now() + 1}`, kind: 'rect', x: 0, y: 82, w: 100, h: 18, opacity: 65 }, 'secondary');
                      const hl = buildBrandText({ text: 'BREAKING STORY', x: 50, y: 9, fontSize: 32, color: '#ffffff', bold: true }, 'primary');
                      const handle = buildBrandText({ id: `t-${Date.now() + 1}`, text: '@yourhandle', x: 50, y: 91, fontSize: 18, color: brandPrimaryColor }, 'secondary');
                      setShapes((p) => [...p, top, bot]); setTexts((p) => [...p, hl, handle]);
                    },
                  },
                  {
                    name: 'Neon Title',
                    desc: 'Bold centered text with vivid neon glow shadow',
                    apply: () => {
                      const txt = buildBrandText(
                        { text: 'NEON TITLE', x: 50, y: 50, fontSize: 56, color: '#ffffff', shadowBlur: 20, shadowColor: brandAccentColor, shadowOffsetX: 0, shadowOffsetY: 0 },
                        'primary'
                      );
                      setTexts((p) => [...p, txt]); setSelectedTextId(txt.id);
                    },
                  },
                  {
                    name: 'Product Tag',
                    desc: 'Small rounded label for product callouts',
                    apply: () => {
                      const badge = buildBrandShape({ kind: 'rect', x: 5, y: 5, w: 30, h: 14, opacity: 100 }, 'accent');
                      const txt = buildBrandText({ text: 'NEW ARRIVAL', x: 20, y: 12, fontSize: 14, color: '#1c1917', bold: true }, 'primary');
                      setShapes((p) => [...p, badge]); setTexts((p) => [...p, txt]);
                    },
                  },
                ].map((tmpl) => (
                  <button key={tmpl.name} onClick={() => { tmpl.apply(); setActiveTool('text'); }}
                    className="group w-full rounded-n border border-n-border bg-white px-3 py-2.5 text-left transition hover:-translate-y-0.5 hover:border-n-green hover:bg-n-hover">
                    <p className="text-sm font-medium text-n-text group-hover:text-n-green">{tmpl.name}</p>
                    <p className="mt-0.5 text-[10px] text-n-text-lt">{tmpl.desc}</p>
                  </button>
                ))}
              </div>
            )}

            {/* EXPORT (when no tool selected) */}
            {!activeTool && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-n-text">{t('editor_export_heading', 'Export & save')}</h3>
                <div>
                  <span className="mb-1.5 block text-[10px] uppercase tracking-wider text-n-text-lt">{t('editor_export_format', 'Format')}</span>
                  <div className="flex gap-1.5">
                    {(['png', 'jpg', 'webp'] as const).map((f) => (
                      <button key={f} onClick={() => setExportFmt(f)}
                        className={`px-3 py-1.5 text-xs rounded uppercase font-medium transition
                          ${exportFmt === f ? 'bg-n-text text-white' : 'bg-white text-n-text-md border border-n-border hover:bg-n-hover'}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                {exportFmt === 'jpg' && (
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-400">Quality</span>
                      <span className="text-gray-500">{exportQuality}%</span>
                    </div>
                    <input type="range" min={10} max={100} value={exportQuality}
                      onChange={(e) => setExportQuality(parseInt(e.target.value))}
                      className="w-full" style={{ accentColor: '#3b82f6' }} />
                  </div>
                )}
                <button onClick={exportImage}
                  className="w-full py-2.5 text-sm font-medium text-white rounded-lg flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 transition">
                  <Download size={15} /> Download
                </button>
                <button
                  onClick={() => saveToLibraryMutation.mutate()}
                  disabled={saveToLibraryMutation.isPending}
                  className="w-full py-2.5 text-sm font-medium rounded-lg flex items-center justify-center gap-2 border border-blue-600 text-blue-400 hover:bg-blue-900/20 disabled:opacity-50 transition">
                  {saveToLibraryMutation.isPending ? (
                    <><Loader2 size={15} className="animate-spin" />{formatBytes(uploadBytes.loaded)} / {formatBytes(uploadBytes.total)}</>
                  ) : (
                    <><Save size={15} />{saveActionLabel}</>
                  )}
                </button>

                {/* AI Captions (Tier 3 #12) ─────────────────────── */}
                <div className="pt-3 mt-1 border-t border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={13} className="text-purple-400" aria-hidden="true" />
                    <h4 className="text-[11px] uppercase tracking-wider font-semibold text-gray-300">
                      {t('editor_captions_heading', 'AI captions')}
                    </h4>
                  </div>

                  <button
                    onClick={() => suggestCaptionsMutation.mutate()}
                    disabled={suggestCaptionsMutation.isPending || !image}
                    aria-busy={suggestCaptionsMutation.isPending}
                    className="w-full py-2 text-sm font-medium rounded-lg flex items-center justify-center gap-2 bg-purple-600/90 text-white hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {suggestCaptionsMutation.isPending ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        {t('editor_captions_generating', 'Analyzing image…')}
                      </>
                    ) : (
                      <>
                        <MessageSquare size={14} />
                        {captions.length > 0
                          ? t('editor_captions_regenerate', 'Regenerate captions')
                          : t('editor_captions_suggest', 'Suggest captions')}
                      </>
                    )}
                  </button>

                  <p className="text-[10px] text-gray-500 mt-1.5 leading-relaxed">
                    {t(
                      'editor_captions_hint',
                      'Analyzes your edited image and suggests ready-to-use captions for social posts.',
                    )}
                  </p>

                  {/* Alt text — shown when available */}
                  {altText && (
                    <div className="mt-3 p-2.5 bg-gray-800/70 rounded border border-gray-700">
                      <p className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">
                        {t('editor_captions_alt_text', 'Alt text')}
                      </p>
                      <p className="text-xs text-gray-300 leading-relaxed">{altText}</p>
                    </div>
                  )}

                  {/* Caption cards */}
                  {captions.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {captions.map((caption, idx) => {
                        const isCopied = copiedCaptionIdx === idx;
                        return (
                          <li
                            key={idx}
                            className="p-2.5 rounded-lg bg-gray-800/70 border border-gray-700 hover:border-purple-600/40 transition-colors"
                          >
                            <p className="text-[12.5px] text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                              {caption}
                            </p>
                            <div className="flex items-center gap-1.5 mt-2">
                              <button
                                onClick={() => handleCopyCaption(caption, idx)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded text-gray-300 hover:bg-gray-700 transition"
                                aria-label={t('editor_captions_copy_label', 'Copy caption {{num}}', { num: idx + 1 })}
                              >
                                {isCopied ? (
                                  <><Check size={11} className="text-green-400" /> {t('editor_captions_copied', 'Copied')}</>
                                ) : (
                                  <><Copy size={11} /> {t('editor_captions_copy', 'Copy')}</>
                                )}
                              </button>
                              <button
                                onClick={() => handleUseCaptionInStudio(caption)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded text-purple-300 hover:bg-purple-900/30 ml-auto transition"
                                aria-label={t('editor_captions_use_label', 'Use caption {{num}} in AI Studio', { num: idx + 1 })}
                              >
                                {t('editor_captions_use_in_studio', 'Use in AI Studio')}
                                <ArrowUpRight size={11} />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <p className="text-[10px] text-gray-600 text-center">Ctrl+S to quick download</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden elements */}
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
      <canvas ref={canvasRef} className="hidden" />

      <MediaPickerModal
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleMediaPick}
        allowedTypes={['image', 'video', 'gif']}
      />
    </div>
  );
};

export default ImageEditorPage;
